import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Marker, Popup, Circle, CircleMarker, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
    AlertTriangle,
    Building2,
    Check,
    Crosshair,
    DollarSign,
    Download,
    Fingerprint,
    History,
    Loader2,
    LocateFixed,
    MapPin,
    Maximize2,
    Minimize2,
    BoxSelect,
    ArrowUpDown,
    Flag,
    Receipt,
    Route as RouteIcon,
    Search,
    ShieldAlert,
    Sparkles,
    Star,
    X,
    Zap
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { Profile } from '../../../types';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import ResponsibleFilter from '../../../src/components/ResponsibleFilter';
import SearchableSelect from '../../../src/components/SearchableSelect';
import RotaConfigModal, { RotaConfig } from './RotaConfigModal';
import { PreviaRota } from './rotaOtimaTypes';
// Mesmo mapeador CNAE→segmento que a edge function usa: é TS puro, sem nada de Deno,
// então a tela importa o próprio arquivo em vez de manter uma cópia que sai de sincronia.
import { segmentoDaLinha } from '../../../supabase/functions/_shared/segmentoCnae';
import CnpjCandidatosModal from './CnpjCandidatosModal';
import MapAutoResize from './MapAutoResize';
import { descobrirCnpj, confirmarCnpjManual, limparCnpjEscolhido, contatosDoCandidato, CnpjCandidato } from '../../../src/services/descobrirCnpj';
import { funilDeVendas } from '../../../src/lib/pipelines';
import { GERAR_ROTA_TERCEIROS_PERMISSION } from '../Rotas/rotaTypes';
import { cruzarGoogleComListaFria, Casamento, PontoListaFria } from '../../../src/lib/cruzamentoGoogleListaFria';
import {
    enriquecerEmpresaRow, enriquecerSociosRow, maskCnpj, carregarStagedPorPlaceIds, importarStagedLead,
    StagedRow, EnriquecimentoData, EnriquecimentoStatus,
} from '../../../src/services/prospeccaoLista';

interface CidadeRef { cidade: string; estado: string; latitude: number; longitude: number; }

interface EmpresaResult {
    place_id: string;
    nome: string;
    categoria: string;
    endereco?: string | null;
    cidade?: string | null;
    estado?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    telefone?: string | null;
    rating?: number | null;
    kwh_estimado?: number | null;
    status: string;
    negociacao_id?: number | null;
    in_crm: boolean;
    cnpj?: string | null;
    enriquecimento_status?: EnriquecimentoStatus;
    enriquecimento_data?: EnriquecimentoData | null;
    /** Situação cadastral conhecida (Lemit ou CNPJ.BIZ). Só a Lista Fria tem. */
    situacao?: string | null;
    /** CNPJ já é lead no CRM: responsável por ele (Lista Fria). */
    dono_crm_id?: string | null;
}

/**
 * Linha da Lista Fria. Na tela ela vem ENXUTA (sem sócios, representante, endereço da
 * Lemit — ver o carregamento do pool): enriquecer ou importar exige recarregar a linha
 * completa antes, senão o que for gravado sai a partir de dado pela metade.
 */
const ehDaListaFria = (e: { place_id: string }) => e.place_id.startsWith('upload:');

/** Linha da Lista Fria que é a mesma empresa de um resultado do Google. */
type LinhaListaFria = PontoListaFria & {
    status: string;
    negociacao_id: number | null;
    documento: string;
    situacao: string | null;
};

/** Situação mais recente entre as duas fontes que temos de graça. */
type SituacaoInfo = { situacao: string; em: string | null };
const situacaoMaisRecente = (a?: SituacaoInfo | null, b?: SituacaoInfo | null): SituacaoInfo | null => {
    if (!a) return b || null;
    if (!b) return a;
    return String(a.em || '') >= String(b.em || '') ? a : b;
};

interface BuscaLog {
    id: string;
    user_id?: string | null;
    cidade?: string | null;
    estado?: string | null;
    raio_km?: number | null;
    categorias?: string[] | null;
    chamadas_api: number;
    resultados: number;
    novos: number;
    custo_estimado_usd: number;
    created_at: string;
}

interface BuscaHistItem {
    id: string;
    user_id?: string | null;
    cidade?: string | null;
    estado?: string | null;
    raio_km?: number | null;
    categorias?: string[] | null;
    resultados: number;
    created_at: string;
}

const GRUPOS_CATEGORIAS: Array<{ grupo: string; itens: Array<{ label: string; categoria: string; emoji: string }> }> = [
    {
        grupo: 'Varejo & Alimentação',
        itens: [
            { label: 'Supermercados', categoria: 'Mercado / Supermercado', emoji: '🛒' },
            { label: 'Açougues', categoria: 'Açougue', emoji: '🥩' },
            { label: 'Restaurantes', categoria: 'Restaurante / Lanchonete', emoji: '🍽️' },
            { label: 'Padarias', categoria: 'Padaria / Confeitaria', emoji: '🥖' },
            { label: 'Sorveterias', categoria: 'Sorveteria', emoji: '🍦' },
            { label: 'Bares', categoria: 'Bar / Botequim', emoji: '🍺' },
            { label: 'Verdurões', categoria: 'Hortifruti / Verdurão', emoji: '🥬' },
            { label: 'Distribuidoras', categoria: 'Distribuidora de Bebidas', emoji: '🍻' },
            { label: 'Atacadistas', categoria: 'Atacadista / Distribuidor', emoji: '📦' },
        ],
    },
    {
        grupo: 'Agronegócio',
        itens: [
            { label: 'Sist. de Irrigação', categoria: 'Sistema de Irrigação', emoji: '💧' },
            { label: 'Laticínios', categoria: 'Laticínio', emoji: '🥛' },
            { label: 'Arm. de Sementes', categoria: 'Armazém de Sementes', emoji: '🌾' },
            { label: 'Produtores Rurais', categoria: 'Produtor Rural', emoji: '🚜' },
        ],
    },
    {
        grupo: 'Saúde',
        itens: [
            { label: 'Clínicas Médicas', categoria: 'Clínica Médica', emoji: '🩺' },
            { label: 'Clín. de Imagem', categoria: 'Clínica de Imagem', emoji: '🩻' },
            { label: 'Laboratórios', categoria: 'Laboratório de Análises', emoji: '🔬' },
            { label: 'Centros de Diálise', categoria: 'Centro de Diálise', emoji: '🫀' },
            { label: 'Hosp. Peq. Porte', categoria: 'Hospital de Pequeno Porte', emoji: '🏥' },
            { label: 'Hospitais', categoria: 'Clínica / Hospital', emoji: '🏥' },
            { label: 'Farmácias', categoria: 'Farmácia / Drogaria', emoji: '💊' },
        ],
    },
    {
        grupo: 'Educação & Hospedagem',
        itens: [
            { label: 'Escolas', categoria: 'Escola / Colégio', emoji: '🏫' },
            { label: 'Faculdades', categoria: 'Faculdade / Universidade', emoji: '🎓' },
            { label: 'Hotéis', categoria: 'Hotel / Pousada', emoji: '🏨' },
            { label: 'Motéis', categoria: 'Motel', emoji: '🏩' },
        ],
    },
    {
        grupo: 'Serviços',
        itens: [
            { label: 'Contabilidade', categoria: 'Escritório de Contabilidade', emoji: '📊' },
            { label: 'Academias', categoria: 'Academia / Esporte', emoji: '🏋️' },
            { label: 'Postos', categoria: 'Posto de Combustível', emoji: '⛽' },
            { label: 'Condomínios', categoria: 'Condomínio', emoji: '🏢' },
        ],
    },
];

// Pin do PONTO DE ORIGEM — visualmente distinto dos resultados (azul, com alvo) e arrastável
const pinOrigem = L.divIcon({
    className: 'vigorgeo-pin-origem',
    html: `<div style="position:relative;width:30px;height:30px;">
        <div style="position:absolute;inset:0;background:#2563eb33;border-radius:9999px;"></div>
        <div style="position:absolute;top:7px;left:7px;width:16px;height:16px;background:#2563eb;border-radius:9999px;border:3px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.45);"></div>
    </div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
});

// Pin dos resultados por status (adendo Bruno): laranja=disponível · azul=selecionado · cinza=importado.
// Desenhado em canvas (CircleMarker + preferCanvas): na Lista Fria o raio traz milhares de
// empresas, e um Marker com divIcon por empresa é um nó de DOM cada — a tela travava.
const PIN_COLOR = { disponivel: '#f97316', selecionado: '#2563eb', importado: '#9ca3af' } as const;
type PinStatus = keyof typeof PIN_COLOR;

/** Distância aproximada em km entre dois pontos (Haversine) — p/ ordenar por proximidade da origem. */
const haversineKm = (a: [number, number], b: [number, number]): number => {
    const R = 6371, toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
};

const FlyTo: React.FC<{ target: [number, number] | null }> = ({ target }) => {
    const map = useMap();
    useEffect(() => { if (target) map.flyTo(target, map.getZoom() < 12 ? 12 : map.getZoom(), { duration: 0.8 }); }, [target?.[0], target?.[1]]);
    return null;
};

// Captura cliques no mapa para reposicionar o ponto de origem.
// O clique que só fecha um popup aberto não conta: o Leaflet fecha o popup no `preclick`
// e o `click` vinha logo atrás, jogando a origem para onde o usuário clicou para fechar.
const MapClickHandler: React.FC<{ onPick: (lat: number, lng: number) => void }> = ({ onPick }) => {
    // `preclick` e `click` saem no mesmo evento do DOM: o popup fechado nele marca a flag,
    // que só zera na próxima volta do event loop.
    const acabouDeFechar = useRef(false);
    useMapEvents({
        popupclose: () => { acabouDeFechar.current = true; setTimeout(() => { acabouDeFechar.current = false; }, 0); },
        click: (e) => { if (!acabouDeFechar.current) onPick(e.latlng.lat, e.latlng.lng); },
    });
    return null;
};

// Observa o enquadramento do mapa (p/ "selecionar pins na área visível" — adendo Bruno).
const BoundsWatcher: React.FC<{ onBounds: (b: L.LatLngBounds) => void }> = ({ onBounds }) => {
    const map = useMapEvents({ moveend: () => onBounds(map.getBounds()), zoomend: () => onBounds(map.getBounds()) });
    useEffect(() => { onBounds(map.getBounds()); }, []);
    return null;
};

const fmtUsd = (v: number) => `US$ ${v.toFixed(3).replace('.', ',')}`;

/** Modal de auditoria: quem buscou, quando, parâmetros e custo estimado da API. */
const UsageLogModal: React.FC<{ isOpen: boolean; onClose: () => void; profiles: Profile[] }> = ({ isOpen, onClose, profiles }) => {
    const [logs, setLogs] = useState<BuscaLog[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        const fetchLogs = async () => {
            setLoading(true);
            const { data, error } = await supabase
                .from('prospeccao_buscas_log')
                .select('id, user_id, cidade, estado, raio_km, categorias, chamadas_api, resultados, novos, custo_estimado_usd, created_at')
                .order('created_at', { ascending: false })
                .limit(100);
            if (error) console.error(error);
            setLogs((data || []) as BuscaLog[]);
            setLoading(false);
        };
        fetchLogs();
    }, [isOpen]);

    const totais = useMemo(() => {
        const now = new Date();
        const iniMes = new Date(now.getFullYear(), now.getMonth(), 1);
        const doMes = logs.filter(l => new Date(l.created_at) >= iniMes);
        return {
            mesBuscas: doMes.length,
            mesChamadas: doMes.reduce((s, l) => s + (l.chamadas_api || 0), 0),
            mesCusto: doMes.reduce((s, l) => s + Number(l.custo_estimado_usd || 0), 0),
            totalCusto: logs.reduce((s, l) => s + Number(l.custo_estimado_usd || 0), 0),
        };
    }, [logs]);

    const nome = (id?: string | null) => {
        if (!id) return 'Desconhecido';
        const p = profiles.find(pr => String(pr.id) === String(id));
        return p?.full_name || '—';
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl">
                            <Receipt size={18} />
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Registro de Uso da API</h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">Google Places · ~US$ 0,032 por chamada (Nearby Search)</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X size={18} />
                    </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 p-3">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase">Buscas no mês</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{totais.mesBuscas}</p>
                    </div>
                    <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 p-3">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase">Chamadas no mês</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{totais.mesChamadas}</p>
                    </div>
                    <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-3">
                        <p className="text-[10px] font-semibold text-emerald-600/70 dark:text-emerald-400/70 uppercase">Custo no mês</p>
                        <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{fmtUsd(totais.mesCusto)}</p>
                    </div>
                    <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 p-3">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase">Custo total (100 últ.)</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{fmtUsd(totais.totalCusto)}</p>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-3">
                    {loading ? (
                        <div className="flex justify-center py-10"><LoadingSpinner label="Carregando registros..." size="sm" /></div>
                    ) : logs.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-10">Nenhuma busca registrada ainda.</p>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-white dark:bg-gray-800">
                                <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <th className="py-2 pr-3 font-semibold">Quando</th>
                                    <th className="py-2 pr-3 font-semibold">Quem</th>
                                    <th className="py-2 pr-3 font-semibold">Região / Raio</th>
                                    <th className="py-2 pr-3 font-semibold">Categorias</th>
                                    <th className="py-2 pr-3 font-semibold text-right">Chamadas</th>
                                    <th className="py-2 pr-3 font-semibold text-right">Result.</th>
                                    <th className="py-2 font-semibold text-right">Custo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(l => (
                                    <tr key={l.id} className="border-b border-gray-50 dark:border-gray-700/50 align-top">
                                        <td className="py-2 pr-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                            {new Date(l.created_at).toLocaleDateString('pt-BR')} {new Date(l.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                        <td className="py-2 pr-3 font-semibold text-gray-800 dark:text-gray-100">{nome(l.user_id)}</td>
                                        <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{l.cidade || '—'}{l.estado ? `-${l.estado}` : ''} · {l.raio_km || '?'} km</td>
                                        <td className="py-2 pr-3 text-gray-500 dark:text-gray-400 max-w-[180px]">{(l.categorias || []).join(', ')}</td>
                                        <td className="py-2 pr-3 text-right font-semibold text-gray-700 dark:text-gray-200">{l.chamadas_api}</td>
                                        <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{l.resultados} <span className="text-gray-400">({l.novos} novos)</span></td>
                                        <td className="py-2 text-right font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">{fmtUsd(Number(l.custo_estimado_usd || 0))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

const ProspeccaoGeoView: React.FC = () => {
    const { profile } = useAuth();
    const { addToast } = useToast();

    const isAllowed = profile?.role === 'adm' || profile?.role === 'gerente'
        || !!profile?.permissions?.includes(GERAR_ROTA_TERCEIROS_PERMISSION);

    const [cidades, setCidades] = useState<CidadeRef[]>([]);
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [cidadeSel, setCidadeSel] = useState<string>('');
    const [raio, setRaio] = useState(5);
    const [maxPorCategoria, setMaxPorCategoria] = useState(20); // limite de resultados por categoria (1–20)
    const [categoriasSel, setCategoriasSel] = useState<string[]>([]);
    const [resultados, setResultados] = useState<EmpresaResult[]>([]);
    /**
     * De qual base vieram os resultados que estão na tela.
     *
     * Sem isso, trocar o seletor para "Lista Fria" com uma busca do Google ainda na tela
     * mandava os leads do GOOGLE (que não têm CNPJ) para a checagem de CNPJ ativo — e a
     * rota morria em "Nenhuma empresa ATIVA na lista curta", como se a base estivesse vazia.
     */
    const [resultadosBase, setResultadosBase] = useState<'google' | 'lista_fria' | null>(null);
    const [buscando, setBuscando] = useState(false);
    const [buscou, setBuscou] = useState(false);
    const [restauradaEm, setRestauradaEm] = useState<string | null>(null);
    const [missingKey, setMissingKey] = useState(false);
    const [importando, setImportando] = useState<string | null>(null);
    const [firstStage, setFirstStage] = useState<{ name: string; pipeline_id: string } | null>(null);
    const [showUsage, setShowUsage] = useState(false);
    const [historico, setHistorico] = useState<BuscaHistItem[]>([]);
    const [abrindoBusca, setAbrindoBusca] = useState<string | null>(null);
    const [vendedoresImport, setVendedoresImport] = useState<string[]>([]); // [] = eu mesmo · multi = distribui round-robin
    const [selPlaces, setSelPlaces] = useState<string[]>([]);
    const [importandoLote, setImportandoLote] = useState(false);
    const [showRotaConfig, setShowRotaConfig] = useState(false);
    const [kwhMin, setKwhMin] = useState<number>(0); // filtro de potencial mínimo (kWh/mês) nos resultados
    // Fonte dos leads da tela: busca paga no Google × CNPJs já carregados na Lista Fria
    const [baseLeads, setBaseLeads] = useState<'google' | 'lista_fria'>('google');
    // Espelho em ref: a restauração da busca salva é assíncrona e termina depois que o
    // usuário já pode ter trocado para a Lista Fria. Lendo o state pelo closure ela via o
    // valor velho e sobrescrevia os resultados vivos com o snapshot antigo.
    const baseLeadsRef = useRef(baseLeads);
    useEffect(() => { baseLeadsRef.current = baseLeads; }, [baseLeads]);
    const [criandoRota, setCriandoRota] = useState(false);
    // Rota: só empresas com CNPJ ativo (gasta 1 crédito CNPJ.BIZ por CNPJ conferido)
    const [somenteAtivas, setSomenteAtivas] = useState(true);
    const [calculandoRota, setCalculandoRota] = useState(false);
    // Prévia calculada — a sequência ainda não foi gravada em lugar nenhum
    const [previaRota, setPreviaRota] = useState<PreviaRota | null>(null);
    // "Quantas dessas N você quer visitar?" — perguntado antes de montar a rota
    const [perguntandoQtd, setPerguntandoQtd] = useState(false);
    const [qtdVisitar, setQtdVisitar] = useState('10');
    // Dia da rota. Era "amanhã" fixo, o que colidia com a rota inteligente já planejada.
    const [dataRotaSel, setDataRotaSel] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
    // Melhorias de tela (adendo Bruno): busca por nome, ordenação, mapa expansível, sync lista↔mapa, seleção por área.
    const [buscaNome, setBuscaNome] = useState('');
    // Distância por padrão: o kWh é a média do segmento (tabela de referência), igual para
    // todas as empresas da mesma categoria — ordenar por ele só agrupava categorias.
    const [sortBy, setSortBy] = useState<'kwh' | 'distancia' | 'rating'>('distancia');
    const [mapaExpandido, setMapaExpandido] = useState(false);
    const [destacado, setDestacado] = useState<string | null>(null);
    // Um popup só, aberto pelo clique no pin — um <Popup> por empresa registrava um
    // listener no mapa para cada uma das milhares da Lista Fria.
    const [popupPlace, setPopupPlace] = useState<string | null>(null);
    const [flyResult, setFlyResult] = useState<[number, number] | null>(null);
    const [mapBounds, setMapBounds] = useState<L.LatLngBounds | null>(null);
    // Descoberta de CNPJ (Casa dos Dados) + enriquecimento Lemit por card
    const [descobrindo, setDescobrindo] = useState<string | null>(null);   // place_id em descoberta
    const [enriquecendo, setEnriquecendo] = useState<string | null>(null); // place_id em enriquecimento Lemit
    const [modalCandidatos, setModalCandidatos] = useState<{ empresa: EmpresaResult; candidatos: CnpjCandidato[] } | null>(null);
    const [escolhendoCnpj, setEscolhendoCnpj] = useState<string | null>(null);
    const [custoCnpj, setCustoCnpj] = useState<number | null>(null);

    // Ponto de origem customizável: centro REAL da busca, independente da "Região".
    // null = usa o centro da região (comportamento padrão).
    const [origem, setOrigem] = useState<{ lat: number; lng: number; label: string } | null>(null);
    const [enderecoInput, setEnderecoInput] = useState('');
    const [sugestoes, setSugestoes] = useState<Array<{ placeId: string; text: string }>>([]);
    // Ponto de CHEGADA (opcional): quando definido, a rota vai de origem→destino e as
    // paradas são escolhidas por menor desvio no caminho, não por proximidade da origem.
    const [destino, setDestino] = useState<{ lat: number; lng: number; label: string } | null>(null);
    const [destinoInput, setDestinoInput] = useState('');
    const [sugestoesDestino, setSugestoesDestino] = useState<Array<{ placeId: string; text: string }>>([]);
    // O que o usuário DIGITOU nos campos de endereço. O campo também é preenchido por
    // código (restaurar a busca salva, escolher a sugestão) e, olhando para ele, cada um
    // desses casos — e cada passo do slider de raio — chamava o autocomplete pago do
    // Google e reabria a lista de sugestões por cima do formulário.
    const [enderecoBusca, setEnderecoBusca] = useState('');
    const [destinoBusca, setDestinoBusca] = useState('');
    const [buscandoEndereco, setBuscandoEndereco] = useState(false);
    const [localizando, setLocalizando] = useState(false);
    // Lista Fria: carregando o pool do recorte (a lista não pode mostrar o número velho)
    const [carregandoPool, setCarregandoPool] = useState(false);
    // Pool do último recorte (centro + raio). Categoria e raio menor refiltram em memória;
    // antes cada clique numa categoria baixava de novo ~10 MB.
    const poolCacheRef = useRef<{ lat: number; lng: number; raio: number; linhas: EmpresaResult[] } | null>(null);
    // documento → situação NÃO ativa mais recente que a Lemit devolveu (carrega uma vez)
    const situacoesLemitRef = useRef<Map<string, SituacaoInfo> | null>(null);
    // documento → responsável da negociação que já existe no CRM (carrega uma vez)
    const docsCrmRef = useRef<Map<string, string | null> | null>(null);
    // Resultado do Google → a mesma empresa na Lista Fria (CNPJ de graça e sem duplicar o lead)
    const [cruzamento, setCruzamento] = useState<Map<string, Casamento<LinhaListaFria>>>(new Map());
    // A lista desenha aos poucos: milhares de cartões de uma vez travavam a tela
    const [limiteLista, setLimiteLista] = useState(100);
    useEffect(() => { setLimiteLista(100); }, [resultados]);

    // Resultados exibidos após o filtro de potencial mínimo (igual ao "kWh mín." das Rotas)
    // e, na Lista Fria, sem os CNPJs que já sabemos inativos quando a caixa está marcada.
    const filtraInativas = resultadosBase === 'lista_fria' && somenteAtivas;
    const ehInativa = (e: EmpresaResult) => !!e.situacao && e.situacao !== 'ATIVA';
    const resultadosVisiveis = useMemo(
        () => resultados.filter(e =>
            (kwhMin <= 0 || Number(e.kwh_estimado || 0) >= kwhMin) && !(filtraInativas && ehInativa(e))),
        [resultados, kwhMin, filtraInativas]
    );
    const inativasOcultas = useMemo(
        () => filtraInativas ? resultados.filter(ehInativa).length : 0,
        [resultados, filtraInativas]
    );

    /** Atualiza status (importado/disponível) dos resultados a partir do pool — consulta grátis ao banco. */
    const refreshStatuses = async (emps: EmpresaResult[]): Promise<EmpresaResult[]> => {
        const ids = emps.map(e => e.place_id);
        if (ids.length === 0) return emps;
        const { data } = await supabase
            .from('empresas_prospectadas')
            .select('place_id, status, negociacao_id, cnpj, enriquecimento_status, enriquecimento_data')
            .in('place_id', ids);
        const map: Record<string, any> = {};
        (data || []).forEach((r: any) => { map[r.place_id] = r; });
        // Mergeia cnpj/enriquecimento p/ sobreviver ao snapshot estático da busca (que não os tem)
        return emps.map(e => map[e.place_id] ? {
            ...e,
            status: map[e.place_id].status,
            negociacao_id: map[e.place_id].negociacao_id,
            cnpj: map[e.place_id].cnpj ?? e.cnpj ?? null,
            enriquecimento_status: map[e.place_id].enriquecimento_status ?? e.enriquecimento_status,
            enriquecimento_data: map[e.place_id].enriquecimento_data ?? e.enriquecimento_data ?? null,
        } : e);
    };

    const fetchHistorico = async () => {
        const { data } = await supabase
            .from('prospeccao_buscas_log')
            .select('id, user_id, cidade, estado, raio_km, categorias, resultados, created_at')
            .gt('resultados', 0)
            .order('created_at', { ascending: false })
            .limit(15);
        setHistorico((data || []) as BuscaHistItem[]);
    };

    /** Reabre uma busca salva (snapshot do banco) — sem nenhuma chamada à API paga. */
    const abrirBusca = async (item: BuscaHistItem) => {
        setAbrindoBusca(item.id);
        try {
            const { data, error } = await supabase
                .from('prospeccao_buscas_log')
                .select('resultados_snapshot, origem_lat, origem_lng, origem_label')
                .eq('id', item.id)
                .single();
            if (error) throw error;
            const snap = (data?.resultados_snapshot || []) as EmpresaResult[];
            const atualizados = await refreshStatuses(snap);
            setResultados(atualizados);
            // Busca salva é sempre do Google: sem isto, reabrir com a Lista Fria marcada
            // deixava a tela achando que os resultados eram da Lista Fria.
            setBaseLeads('google');
            setResultadosBase('google');
            setBuscou(true);
            setMissingKey(false);
            setRestauradaEm(item.created_at);
            if (item.cidade) setCidadeSel(`${item.cidade}|${item.estado || ''}`);
            if (item.raio_km) setRaio(Number(item.raio_km));
            if (Array.isArray(item.categorias)) setCategoriasSel(item.categorias as string[]);
            // Reproduz o mesmo recorte geográfico: restaura o pin de origem (ou volta ao centro da região)
            if (data?.origem_lat != null && data?.origem_lng != null) {
                setOrigem({ lat: Number(data.origem_lat), lng: Number(data.origem_lng), label: data.origem_label || 'Origem salva' });
                setEnderecoInput(data.origem_label || '');
            } else {
                limparOrigem();
            }
        } catch (err) {
            console.error('Erro ao abrir busca salva:', err);
            addToast('Erro ao abrir a busca salva.', 'error');
        } finally {
            setAbrindoBusca(null);
        }
    };

    useEffect(() => {
        const init = async () => {
            const [{ data: cc }, { data: pipes }, { data: profs }, { data: cfg }] = await Promise.all([
                supabase.from('cidades_coordenadas').select('cidade, estado, latitude, longitude').not('latitude', 'is', null).order('cidade'),
                supabase.from('pipelines').select('id, name, pipeline_stages(name, position)').order('created_at').order('id'),
                supabase.from('profiles').select('*').neq('role', 'consumidor'),
                supabase.from('system_settings').select('value').eq('key', 'casadosdados_custo_consulta').maybeSingle(),
            ]);
            if (cfg?.value) setCustoCnpj(Number(String(cfg.value).replace(',', '.')) || null);
            if (cc) {
                setCidades(cc as CidadeRef[]);
                const goiania = (cc as CidadeRef[]).find(c => c.cidade.toLowerCase().includes('goi'));
                if (goiania) setCidadeSel(`${goiania.cidade}|${goiania.estado}`);
            }
            if (profs) setProfiles(profs);
            // Primeira etapa do Funil de Vendas p/ leads importados
            // funilDeVendas em vez de /venda/i: esse regex casa com "Pós-Venda" e, com os
            // dois funis empatados em created_at, o lead importado podia nascer lá.
            const vendas = funilDeVendas(pipes as any[]);
            if (vendas) {
                const stages = ((vendas as any).pipeline_stages || []).sort((a: any, b: any) => a.position - b.position);
                if (stages[0]) setFirstStage({ name: stages[0].name, pipeline_id: (vendas as any).id });
            }

            // Restaura a última busca do usuário (persistência ao fechar/reabrir)
            if (profile) {
                const { data: ultima } = await supabase
                    .from('prospeccao_buscas_log')
                    .select('cidade, estado, raio_km, categorias, resultados_snapshot, created_at, origem_lat, origem_lng, origem_label')
                    .eq('user_id', profile.id)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle();
                if (ultima?.resultados_snapshot && Array.isArray(ultima.resultados_snapshot) && ultima.resultados_snapshot.length > 0
                    // Já trocou para a Lista Fria enquanto isto carregava: não atropela
                    && baseLeadsRef.current === 'google') {
                    const atualizados = await refreshStatuses(ultima.resultados_snapshot as EmpresaResult[]);
                    setResultados(atualizados);
                    // Busca salva é sempre do Google (a Lista Fria não gera log — não paga API).
                    // Sem marcar a origem, a rota mandaria esses leads como se fossem da base
                    // selecionada e o rótulo "Busca salva restaurada" ficaria por cima dos
                    // resultados vivos da Lista Fria.
                    setResultadosBase('google');
                    setBuscou(true);
                    setRestauradaEm(ultima.created_at);
                    if (ultima.cidade) setCidadeSel(`${ultima.cidade}|${ultima.estado || ''}`);
                    if (ultima.raio_km) setRaio(Number(ultima.raio_km));
                    if (Array.isArray(ultima.categorias)) setCategoriasSel(ultima.categorias as string[]);
                    if (ultima.origem_lat != null && ultima.origem_lng != null) {
                        setOrigem({ lat: Number(ultima.origem_lat), lng: Number(ultima.origem_lng), label: ultima.origem_label || 'Origem salva' });
                        setEnderecoInput(ultima.origem_label || '');
                    }
                }
            }
            fetchHistorico();
        };
        init();
    }, [profile?.id]);

    const centro = useMemo((): [number, number] | null => {
        if (!cidadeSel) return null;
        const [cid, est] = cidadeSel.split('|');
        const c = cidades.find(x => x.cidade === cid && (x.estado || '') === (est || ''));
        return c ? [Number(c.latitude), Number(c.longitude)] : null;
    }, [cidadeSel, cidades]);

    // Centro REAL da busca: o pin de origem quando definido, senão o centro da região
    const centroEfetivo = useMemo((): [number, number] | null => (
        origem ? [origem.lat, origem.lng] : centro
    ), [origem, centro]);

    // Resultados após busca por nome + ordenação (kWh / distância da origem / avaliação) — adendo Bruno.
    const resultadosView = useMemo(() => {
        const q = buscaNome.trim().toLowerCase();
        let arr = q ? resultadosVisiveis.filter(e => `${e.nome} ${e.categoria || ''}`.toLowerCase().includes(q)) : resultadosVisiveis.slice();
        // Com rota calculada, a ordem da lista É a ordem de visita — as paradas sobem para o
        // topo na sequência e o resto segue pela ordenação escolhida.
        if (previaRota) {
            const pos = new Map<string, number>();
            previaRota.paradas.forEach(p => { if (p.place_id) pos.set(p.place_id, p.posicao); });
            if (pos.size > 0) {
                return arr.sort((a, b) => {
                    const pa = pos.get(a.place_id) ?? Infinity;
                    const pb = pos.get(b.place_id) ?? Infinity;
                    if (pa !== pb) return pa - pb;
                    return Number(b.kwh_estimado || 0) - Number(a.kwh_estimado || 0);
                });
            }
        }
        arr.sort((a, b) => {
            if (sortBy === 'rating') return Number(b.rating || 0) - Number(a.rating || 0);
            if (sortBy === 'distancia' && centroEfetivo) {
                const da = a.latitude && a.longitude ? haversineKm(centroEfetivo, [a.latitude, a.longitude]) : Infinity;
                const db = b.latitude && b.longitude ? haversineKm(centroEfetivo, [b.latitude, b.longitude]) : Infinity;
                return da - db;
            }
            return Number(b.kwh_estimado || 0) - Number(a.kwh_estimado || 0); // 'kwh' (padrão)
        });
        return arr;
    }, [resultadosVisiveis, buscaNome, sortBy, centroEfetivo, previaRota]);

    /** place_id → posição na rota calculada. Vazio quando não há prévia. */
    const posicaoNaRota = useMemo(() => {
        const m = new Map<string, number>();
        previaRota?.paradas.forEach(p => { if (p.place_id) m.set(p.place_id, p.posicao); });
        return m;
    }, [previaRota]);

    const statusPin = (e: EmpresaResult): PinStatus =>
        e.status === 'importado' || e.in_crm ? 'importado' : selPlaces.includes(e.place_id) ? 'selecionado' : 'disponivel';

    // Seleciona todos os importáveis dentro da área visível do mapa (adendo Bruno).
    const selecionarNaArea = () => {
        if (!mapBounds) return;
        const naArea = resultadosView.filter(e =>
            e.status !== 'importado' && !e.in_crm && e.latitude != null && e.longitude != null &&
            mapBounds.contains(L.latLng(e.latitude, e.longitude)));
        if (naArea.length === 0) { addToast('Nenhum lead importável na área visível do mapa.', 'info'); return; }
        setSelPlaces(prev => Array.from(new Set([...prev, ...naArea.map(e => e.place_id)])));
        addToast(`${naArea.length} lead(s) da área selecionado(s).`, 'success');
    };

    // Sincronização lista → mapa: destaca e centraliza o pin do card.
    const focarNoMapa = (e: EmpresaResult) => {
        setDestacado(e.place_id);
        if (e.latitude != null && e.longitude != null) setFlyResult([e.latitude, e.longitude]);
    };

    // Define a origem por clique/arraste no mapa (sem reverse geocode → rotula com coordenadas)
    const definirOrigemPorMapa = (lat: number, lng: number) => {
        setOrigem({ lat, lng, label: `Ponto no mapa (${lat.toFixed(4)}, ${lng.toFixed(4)})` });
        setSugestoes([]);
    };

    const usarMinhaLocalizacao = () => {
        if (!navigator.geolocation) { addToast('Geolocalização indisponível neste dispositivo.', 'error'); return; }
        setLocalizando(true);
        navigator.geolocation.getCurrentPosition(
            pos => {
                setOrigem({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: 'Minha localização' });
                setSugestoes([]);
                setEnderecoInput('');
                setEnderecoBusca('');
                setLocalizando(false);
                addToast('Origem definida na sua localização atual 📍', 'success');
            },
            () => { setLocalizando(false); addToast('Não foi possível obter sua localização.', 'error'); },
            { enableHighAccuracy: true, timeout: 8000 }
        );
    };

    const limparOrigem = () => {
        setOrigem(null);
        setEnderecoInput('');
        setEnderecoBusca('');
        setSugestoes([]);
    };

    // Autocomplete de endereço (Google Places via edge function), com debounce e viés na região atual.
    // Só reage ao que foi digitado: centro e raio entram como viés, não como gatilho.
    useEffect(() => {
        const q = enderecoBusca.trim();
        if (q.length < 3) { setSugestoes([]); return; }
        let cancel = false;
        setBuscandoEndereco(true);
        const t = setTimeout(async () => {
            try {
                const { data, error } = await supabase.functions.invoke('geocode-search', {
                    body: { action: 'autocomplete', input: q, lat: centroEfetivo?.[0] ?? null, lng: centroEfetivo?.[1] ?? null, raioKm: raio },
                });
                if (cancel) return;
                if (error) { setSugestoes([]); return; }
                setSugestoes((data?.suggestions || []) as Array<{ placeId: string; text: string }>);
            } catch (_) {
                if (!cancel) setSugestoes([]);
            } finally {
                if (!cancel) setBuscandoEndereco(false);
            }
        }, 350);
        return () => { cancel = true; clearTimeout(t); };
    }, [enderecoBusca]);

    const escolherSugestao = async (s: { placeId: string; text: string }) => {
        setEnderecoInput(s.text);
        setEnderecoBusca('');
        setSugestoes([]);
        try {
            const { data, error } = await supabase.functions.invoke('geocode-search', {
                body: { action: 'details', placeId: s.placeId },
            });
            if (error || !data?.lat) throw error || new Error('Sem coordenadas');
            setOrigem({ lat: Number(data.lat), lng: Number(data.lng), label: data.label || s.text });
        } catch (err) {
            console.error('Erro ao obter coordenadas do endereço:', err);
            addToast('Não foi possível localizar esse endereço.', 'error');
        }
    };

    // Autocomplete do ponto de CHEGADA — mesmo mecanismo da origem, estado separado para
    // os dois campos não brigarem pela lista de sugestões.
    useEffect(() => {
        const q = destinoBusca.trim();
        if (q.length < 3) { setSugestoesDestino([]); return; }
        let cancel = false;
        const t = setTimeout(async () => {
            try {
                const { data, error } = await supabase.functions.invoke('geocode-search', {
                    body: { action: 'autocomplete', input: q, lat: centroEfetivo?.[0] ?? null, lng: centroEfetivo?.[1] ?? null, raioKm: raio },
                });
                if (cancel) return;
                setSugestoesDestino(error ? [] : ((data?.suggestions || []) as Array<{ placeId: string; text: string }>));
            } catch (_) {
                if (!cancel) setSugestoesDestino([]);
            }
        }, 350);
        return () => { cancel = true; clearTimeout(t); };
    }, [destinoBusca]);

    /**
     * Situação cadastral que a Lemit já devolveu no enriquecimento — dado pago, sem custo
     * novo. Vêm só os documentos com alguma consulta NÃO ativa (~270) e, para esses, a
     * consulta mais recente de cada um (um CNPJ inapto pode ter sido regularizado depois).
     */
    const carregarSituacoesLemit = async (): Promise<Map<string, SituacaoInfo>> => {
        if (situacoesLemitRef.current) return situacoesLemitRef.current;
        const mapa = new Map<string, SituacaoInfo>();
        const { data: naoAtivas, error } = await supabase
            .from('lemit_consultas')
            .select('documento')
            .neq('resultado->>situacao', 'ATIVA');
        if (error) { console.error('Erro ao ler a situação cadastral da Lemit:', error); return mapa; }
        const docs = Array.from(new Set((naoAtivas || []).map((r: any) => String(r.documento || '')).filter(Boolean)));
        for (let i = 0; i < docs.length; i += 100) {
            const { data } = await supabase
                .from('lemit_consultas')
                .select('documento, situacao:resultado->>situacao, created_at')
                .in('documento', docs.slice(i, i + 100));
            (data || []).forEach((r: any) => {
                if (!r.situacao) return;
                const atual = mapa.get(r.documento);
                if (!atual || String(r.created_at) > String(atual.em)) mapa.set(r.documento, { situacao: r.situacao, em: r.created_at });
            });
        }
        situacoesLemitRef.current = mapa;
        return mapa;
    };

    /**
     * CPF/CNPJ que já são lead no CRM. A Lista Fria marca "disponível" quem nunca foi
     * importado por ela, mas o mesmo CNPJ pode ter entrado no CRM por outro caminho — a
     * aba Lista Fria já bloqueava ("Duplicado no CRM"); aqui não, e o lead duplicava.
     * O `cpf_cnpj` do CRM pode ter máscara: compara só os dígitos.
     */
    const carregarDocsCrm = async (): Promise<Map<string, string | null>> => {
        if (docsCrmRef.current) return docsCrmRef.current;
        const mapa = new Map<string, string | null>();
        for (let from = 0; ; from += 1000) {
            const { data, error } = await supabase
                .from('negociacoes')
                .select('id, cpf_cnpj, responsavel_id')
                .not('cpf_cnpj', 'is', null)
                .order('id', { ascending: true })
                .range(from, from + 999);
            if (error) { console.error('Erro ao ler os documentos do CRM:', error); return mapa; }
            (data || []).forEach((n: any) => {
                const d = String(n.cpf_cnpj || '').replace(/\D/g, '');
                if (d.length === 11 || d.length === 14) mapa.set(d, n.responsavel_id ?? null);
            });
            if ((data || []).length < 1000) break;
        }
        docsCrmRef.current = mapa;
        return mapa;
    };

    /**
     * Lista Fria: mostra os leads no mapa assim que a categoria/raio mudam.
     *
     * Aqui não há busca paga — os leads já estão no banco —, então esperar o usuário
     * clicar em "Gerar rota otimizada" para só então ver alguma coisa no mapa não fazia
     * sentido. A consulta é ao Postgres (grátis).
     *
     * Vem ENXUTA: só o CNAE (segmento), a checagem de CNPJ e os contatos do JSON da Lemit.
     * O JSON inteiro tem ~5 KB por linha (sócios com o cadastro completo do CPF) e o raio
     * de 9 km baixava 10 MB a cada clique numa categoria.
     */
    useEffect(() => {
        if (baseLeads !== 'lista_fria' || !centroEfetivo) return;
        let cancel = false;
        const [lat, lng] = centroEfetivo;

        const aplicarFiltro = (pool: EmpresaResult[]) => {
            const alvo = new Set(categoriasSel);
            const dentro = pool.filter(e =>
                haversineKm([lat, lng], [e.latitude!, e.longitude!]) <= raio
                // Sem categoria marcada, mostra tudo o que está no raio
                && (alvo.size === 0 || alvo.has(e.categoria)));
            setResultados(dentro);
            setResultadosBase('lista_fria');
            setBuscou(true);
            // Estes leads são da consulta viva, não de busca salva — senão a tela seguia
            // dizendo "Busca salva restaurada (13:02)" por cima de resultados novos.
            setRestauradaEm(null);
        };

        // Mesmo centro e raio menor ou igual ao já baixado: só refiltra
        const cache = poolCacheRef.current;
        if (cache && cache.lat === lat && cache.lng === lng && raio <= cache.raio) {
            aplicarFiltro(cache.linhas);
            return;
        }

        const t = setTimeout(async () => {
            setCarregandoPool(true);
            const grauLat = raio / 111;
            const grauLng = raio / (111 * Math.cos(lat * Math.PI / 180) || 1);
            // Paginado: o PostgREST corta a resposta em 1000 linhas e a caixa de 45 km tem
            // ~4.500 leads. Buscando de uma vez, o filtro de segmento rodava sobre 22% da
            // área — "Motéis" mostrava 6 de 23. O corte tem que vir DEPOIS do filtro.
            const PAGE = 1000;
            const brutos: any[] = [];
            // em paralelo com as páginas
            const situacoesP = carregarSituacoesLemit();
            const docsCrmP = carregarDocsCrm();
            for (let from = 0; from < 20000; from += PAGE) {
                const { data, error } = await supabase
                    .from('empresas_prospectadas')
                    .select('id, place_id, nome, cnpj, documento, categoria, endereco, cidade, bairro, telefone, latitude, longitude, kwh_estimado, rating, enriquecimento_status, ' +
                        'cnae:enriquecimento_data->cnae, cnpjbiz:enriquecimento_data->cnpjbiz, ' +
                        'melhor_whatsapp:enriquecimento_data->>melhor_whatsapp, melhor_email:enriquecimento_data->>melhor_email')
                    .eq('fonte', 'upload')
                    .eq('status', 'disponivel')
                    .not('latitude', 'is', null)
                    .gte('latitude', lat - grauLat).lte('latitude', lat + grauLat)
                    .gte('longitude', lng - grauLng).lte('longitude', lng + grauLng)
                    // `id` como desempate: sem ORDER estável o range se sobrepõe e pula linhas
                    .order('id', { ascending: true })
                    .range(from, from + PAGE - 1);
                if (cancel) { setCarregandoPool(false); return; }
                if (error) { console.error('Erro ao carregar leads da Lista Fria:', error); setCarregandoPool(false); return; }
                brutos.push(...(data || []));
                if ((data || []).length < PAGE) break;
            }
            const [situacoesLemit, docsCrm] = await Promise.all([situacoesP, docsCrmP]);
            if (cancel) { setCarregandoPool(false); return; }

            const linhas: EmpresaResult[] = brutos.map((e: any) => {
                const enr: EnriquecimentoData = {
                    // Enxuto: o serviço recarrega a linha completa antes de gravar qualquer coisa
                    _parcial: true,
                    cnae: e.cnae ?? undefined,
                    cnpjbiz: e.cnpjbiz ?? undefined,
                    melhor_whatsapp: e.melhor_whatsapp ?? undefined,
                    melhor_email: e.melhor_email ?? undefined,
                };
                const doc = String(e.documento || e.cnpj || '').replace(/\D/g, '');
                // Situação: a checagem mais recente entre CNPJ.BIZ (feita na rota) e Lemit.
                // Linha enriquecida sem nada não-ativo na Lemit = a Lemit a viu ATIVA.
                const cnpjbiz = e.cnpjbiz?.situacao ? { situacao: String(e.cnpjbiz.situacao), em: e.cnpjbiz.verificado_em || null } : null;
                const lemit = situacoesLemit.get(doc)
                    || (doc.length === 14 && ['empresa', 'socios', 'completo'].includes(e.enriquecimento_status) ? { situacao: 'ATIVA', em: null } : null);
                const sit = situacaoMaisRecente(cnpjbiz, lemit);
                return {
                    ...e,
                    latitude: Number(e.latitude),
                    longitude: Number(e.longitude),
                    // `categoria` da Lista Fria é a natureza jurídica ("Sociedade Empresária
                    // Limitada"); o ramo de verdade sai do CNAE.
                    categoria: segmentoDaLinha({ categoria: e.categoria, enriquecimento_data: enr }) || 'Ramo não identificado',
                    enriquecimento_data: enr,
                    situacao: sit?.situacao ?? null,
                    status: 'disponivel',
                    in_crm: docsCrm.has(doc),
                    dono_crm_id: docsCrm.get(doc) ?? null,
                };
            });
            poolCacheRef.current = { lat, lng, raio, linhas };
            setCarregandoPool(false);
            if (!cancel) aplicarFiltro(linhas);
        }, 300);
        return () => { cancel = true; clearTimeout(t); };
    }, [baseLeads, centroEfetivo?.[0], centroEfetivo?.[1], raio, categoriasSel]);

    /**
     * Cruza o resultado do Google com a Lista Fria da mesma área (só leitura).
     *
     * A empresa que já está na Lista Fria ganha o CNPJ e a situação sem a busca paga da
     * Casa dos Dados, e não pode mais entrar duas vezes no CRM: se já foi importada por
     * lá, fica bloqueada aqui; se não, o "Importar" daqui usa a linha da Lista Fria.
     * A chave são os place_ids — mudar status/seleção não refaz a consulta.
     */
    const chaveGoogle = useMemo(
        () => (resultadosBase === 'google' ? resultados.map(e => e.place_id).sort().join(',') : ''),
        [resultados, resultadosBase],
    );
    useEffect(() => {
        if (!chaveGoogle) { setCruzamento(new Map()); return; }
        const google = resultados.filter(e => e.latitude != null && e.longitude != null);
        if (google.length === 0) return;
        let cancel = false;
        (async () => {
            const lats = google.map(e => e.latitude!), lngs = google.map(e => e.longitude!);
            const folga = 0.003; // ~300 m, o maior raio de casamento
            const linhas: any[] = [];
            for (let from = 0; from < 20000; from += 1000) {
                const { data, error } = await supabase
                    .from('empresas_prospectadas')
                    .select('id, place_id, nome, telefone, status, negociacao_id, documento, cnpj, latitude, longitude, ' +
                        'nome_fantasia:raw_payload->>nomeFantasia, razao_social:raw_payload->>razaoSocial, cnpjbiz:enriquecimento_data->cnpjbiz')
                    .eq('fonte', 'upload')
                    .not('latitude', 'is', null)
                    .gte('latitude', Math.min(...lats) - folga).lte('latitude', Math.max(...lats) + folga)
                    .gte('longitude', Math.min(...lngs) - folga).lte('longitude', Math.max(...lngs) + folga)
                    .order('id', { ascending: true })
                    .range(from, from + 999);
                if (cancel || error) return;
                linhas.push(...(data || []));
                if ((data || []).length < 1000) break;
            }
            const situacoesLemit = await carregarSituacoesLemit();
            if (cancel) return;
            const pool: LinhaListaFria[] = linhas.map((r: any) => {
                const doc = String(r.documento || r.cnpj || '').replace(/\D/g, '');
                const biz = r.cnpjbiz?.situacao ? { situacao: String(r.cnpjbiz.situacao), em: r.cnpjbiz.verificado_em || null } : null;
                return {
                    place_id: r.place_id, nome: r.nome, nome_fantasia: r.nome_fantasia, razao_social: r.razao_social,
                    telefone: r.telefone, latitude: Number(r.latitude), longitude: Number(r.longitude),
                    status: r.status, negociacao_id: r.negociacao_id ?? null, documento: doc,
                    situacao: situacaoMaisRecente(biz, situacoesLemit.get(doc))?.situacao ?? null,
                };
            });
            const casou = cruzarGoogleComListaFria(google, pool);
            setCruzamento(casou);
            // Já importada pela Lista Fria = já é lead: some dos importáveis daqui
            setResultados(prev => prev.map(e => {
                const c = casou.get(e.place_id);
                return c && c.listaFria.status === 'importado' && !e.in_crm ? { ...e, in_crm: true } : e;
            }));
        })();
        return () => { cancel = true; };
    }, [chaveGoogle]);

    const escolherDestino = async (s: { placeId: string; text: string }) => {
        setDestinoInput(s.text);
        setDestinoBusca('');
        setSugestoesDestino([]);
        try {
            const { data, error } = await supabase.functions.invoke('geocode-search', {
                body: { action: 'details', placeId: s.placeId },
            });
            if (error || !data?.lat) throw error || new Error('Sem coordenadas');
            setDestino({ lat: Number(data.lat), lng: Number(data.lng), label: data.label || s.text });
        } catch (err) {
            console.error('Erro ao obter coordenadas do destino:', err);
            addToast('Não foi possível localizar esse endereço de chegada.', 'error');
        }
    };

    const toggleCategoria = (cat: string) => {
        setCategoriasSel(prev => prev.includes(cat)
            ? prev.filter(c => c !== cat)
            : [...prev, cat]);
    };

    const buscar = async () => {
        if (!centroEfetivo) { addToast('Selecione a região ou defina um ponto de origem.', 'error'); return; }
        if (categoriasSel.length === 0) { addToast('Selecione ao menos uma categoria.', 'error'); return; }
        setBuscando(true);
        setMissingKey(false);
        setRestauradaEm(null);
        try {
            const [cid, est] = cidadeSel.split('|');
            const { data, error } = await supabase.functions.invoke('buscar-empresas', {
                body: {
                    lat: centroEfetivo[0], lng: centroEfetivo[1], raioKm: raio, categorias: categoriasSel,
                    cidade: cid || null, estado: est || null, maxResultados: maxPorCategoria,
                    // Persiste a origem real (pin custom) p/ reproduzir o recorte ao reabrir a busca
                    origemLat: origem?.lat ?? null, origemLng: origem?.lng ?? null, origemLabel: origem?.label ?? null,
                },
            });
            if (error) {
                const ctx = (error as any)?.context;
                if (ctx?.status === 422) { setMissingKey(true); setResultados([]); setBuscou(true); return; }
                if (ctx?.status === 403) { addToast('Apenas administradores e gerentes podem usar a Prospecção.', 'error'); return; }
                throw error;
            }
            if (data?.error === 'missing_key') { setMissingKey(true); setResultados([]); setBuscou(true); return; }
            setResultados((data?.empresas || []) as EmpresaResult[]);
            setResultadosBase('google');
            setBuscou(true);
            addToast(
                `${data?.empresas?.length || 0} empresas encontradas · ${data?.chamadas_api || 0} chamadas (~${fmtUsd(Number(data?.custo_estimado_usd || 0))})`
                + (data?.fechadas ? ` · ${data.fechadas} fechada(s) de vez no Google, fora da lista` : ''),
                'success',
            );
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'prospeccao', acao: 'buscar_empresas', origem: 'acao' });
            fetchHistorico();
        } catch (err) {
            console.error('Erro na busca de empresas:', err);
            addToast('Erro ao buscar empresas. Verifique a configuração da API.', 'error');
        } finally {
            setBuscando(false);
        }
    };

    // Destinos da importação: vazio = eu mesmo; vários = distribui as empresas round-robin entre eles
    const destinos = (): string[] => vendedoresImport.length ? vendedoresImport : (profile ? [profile.id] : []);
    const nomeCurto = (id: string) => profiles.find(p => String(p.id) === id)?.full_name?.split(' ')[0] || 'vendedor';
    const destNome = vendedoresImport.length === 0
        ? 'você'
        : vendedoresImport.length === 1
            ? nomeCurto(vendedoresImport[0])
            : `${vendedoresImport.length} vendedores`;

    /** Cria a negociação e marca a empresa no pool. Retorna o id da negociação ou null. */
    const importarCore = async (empresa: EmpresaResult, responsavelOverride?: string): Promise<number | null> => {
        if (!profile || !firstStage) return null;
        const responsavelId = responsavelOverride || vendedoresImport[0] || profile.id;
        // CNPJ + contatos vindos da descoberta/enriquecimento (quando houver) — espelha importarStagedLead
        const escolhido = empresa.enriquecimento_data?.cnpj_descoberta?.escolhido;
        const enr = empresa.enriquecimento_data;
        try {
            // Lista Fria: importa pela linha completa, o mesmo caminho da aba Lista Fria.
            // Montando o lead daqui ele nascia sem representante legal, sem WhatsApp na rota
            // e com a natureza jurídica no lugar do segmento.
            // Empresa do Google que também está na Lista Fria: o lead nasce de lá (com CNPJ,
            // sócios e contatos) e as duas linhas do pool ficam marcadas como importadas.
            const gemea = !ehDaListaFria(empresa) ? cruzamento.get(empresa.place_id)?.listaFria : undefined;
            if (ehDaListaFria(empresa) || gemea) {
                const placeListaFria = gemea ? gemea.place_id : empresa.place_id;
                const [row] = await carregarStagedPorPlaceIds([placeListaFria]);
                if (!row) throw new Error('Lead não encontrado na Lista Fria.');
                if (row.status === 'importado') throw new Error('Este lead já foi importado por outra pessoa.');
                const negId = await importarStagedLead(row, responsavelId, profile.id, firstStage, 'Prospecção geográfica');
                if (negId && gemea) {
                    await supabase.from('empresas_prospectadas')
                        .update({ status: 'importado', negociacao_id: negId, importado_por: profile.id, updated_at: new Date().toISOString() })
                        .eq('place_id', empresa.place_id);
                }
                if (negId) {
                    setResultados(prev => prev.map(e => e.place_id === empresa.place_id
                        ? { ...e, status: 'importado', negociacao_id: negId } : e));
                }
                return negId;
            }

            const { data: neg, error } = await supabase
                .from('negociacoes')
                .insert([{
                    titulo: empresa.nome,
                    cliente_nome: escolhido?.razao_social || empresa.nome,
                    stage: firstStage.name,
                    status: 'em_andamento',
                    pipeline_id: firstStage.pipeline_id,
                    consumo_medio_kwh: empresa.kwh_estimado || 0,
                    segmento: empresa.categoria,
                    tipo_cliente: empresa.cnpj ? 'PJ' : undefined,
                    cpf_cnpj: empresa.cnpj || undefined,
                    cidade: empresa.cidade,
                    bairro: (empresa as any).bairro ?? undefined,
                    whatsapp: enr?.melhor_whatsapp || empresa.telefone,
                    email: enr?.melhor_email || undefined,
                    latitude: empresa.latitude,
                    longitude: empresa.longitude,
                    geocode_status: empresa.latitude ? 'endereco' : 'pendente',
                    origem: 'Prospecção geográfica',
                    responsavel_id: responsavelId,
                }])
                .select()
                .single();
            if (error) throw error;

            await supabase.from('empresas_prospectadas')
                .update({ status: 'importado', negociacao_id: neg.id, importado_por: profile.id, updated_at: new Date().toISOString() })
                .eq('place_id', empresa.place_id);

            setResultados(prev => prev.map(e => e.place_id === empresa.place_id
                ? { ...e, status: 'importado', negociacao_id: neg.id } : e));
            return neg.id;
        } catch (err) {
            console.error('Erro ao importar lead:', err);
            return null;
        }
    };

    const importar = async (empresa: EmpresaResult) => {
        setImportando(empresa.place_id);
        const alvo = destinos()[0];
        const negId = await importarCore(empresa, alvo);
        setImportando(null);
        if (negId) {
            const nome = vendedoresImport.length ? nomeCurto(alvo) : 'você';
            addToast(`"${empresa.nome}" importado como lead para ${nome}! 🎉`, 'success');
        } else {
            addToast('Erro ao importar como lead.', 'error');
        }
    };

    // ── Descoberta de CNPJ (Casa dos Dados) + enriquecimento Lemit ──────────────
    const aplicar = (placeId: string, patch: Partial<EmpresaResult>) =>
        setResultados(prev => prev.map(e => e.place_id === placeId ? { ...e, ...patch } : e));

    /** Monta uma StagedRow a partir da EmpresaResult p/ reusar o pipeline Lemit da Lista Fria. */
    const rowFromEmpresa = (e: EmpresaResult): StagedRow => ({
        id: '', place_id: e.place_id, cnpj: e.cnpj || '',
        nome: e.nome, razao_social: e.enriquecimento_data?.cnpj_descoberta?.escolhido?.razao_social || e.nome,
        categoria: e.categoria, endereco: e.endereco || '', cidade: e.cidade || null, estado: e.estado || null,
        bairro: null, cep: null, email: e.enriquecimento_data?.melhor_email || null,
        telefone: e.enriquecimento_data?.melhor_whatsapp || e.telefone || null,
        latitude: e.latitude ?? null, longitude: e.longitude ?? null,
        status: e.status, negociacao_id: e.negociacao_id ?? null,
        enriquecimento_status: e.enriquecimento_status || 'nao',
        enriquecimento_data: e.enriquecimento_data || null, raw_payload: null,
    });

    const descobrir = async (e: EmpresaResult) => {
        if (e.cnpj) return;
        // Cache local: candidatos já buscados (sem escolhido) reabrem o modal sem custo
        const cache = e.enriquecimento_data?.cnpj_descoberta;
        if (cache?.candidatos?.length && !cache.escolhido) {
            setModalCandidatos({ empresa: e, candidatos: cache.candidatos as CnpjCandidato[] });
            return;
        }
        setDescobrindo(e.place_id);
        try {
            const r = await descobrirCnpj(e.place_id);
            if (r.auto) {
                const c = contatosDoCandidato(r.auto);
                const enr: EnriquecimentoData = {
                    ...(e.enriquecimento_data || {}),
                    ...(c.melhor_whatsapp && !e.enriquecimento_data?.melhor_whatsapp ? { melhor_whatsapp: c.melhor_whatsapp } : {}),
                    ...(c.melhor_email && !e.enriquecimento_data?.melhor_email ? { melhor_email: c.melhor_email } : {}),
                    cnpj_descoberta: { consultado_em: new Date().toISOString(), estrategia: r.estrategia, total: r.candidatos.length, candidatos: r.candidatos, escolhido: r.auto, escolhido_por: 'auto' },
                };
                aplicar(e.place_id, { cnpj: r.auto.cnpj, enriquecimento_data: enr });
                addToast(`CNPJ encontrado: ${r.auto.razao_social} · ${maskCnpj(r.auto.cnpj)}`, 'success');
            } else if (r.candidatos.length > 0) {
                aplicar(e.place_id, { enriquecimento_data: { ...(e.enriquecimento_data || {}), cnpj_descoberta: { consultado_em: new Date().toISOString(), estrategia: r.estrategia, total: r.candidatos.length, candidatos: r.candidatos } } });
                setModalCandidatos({ empresa: e, candidatos: r.candidatos });
            } else {
                addToast(r.motivo || 'CNPJ não encontrado para esta empresa na Casa dos Dados.', 'info');
            }
        } catch (err: any) {
            addToast(err.message || 'Erro ao descobrir o CNPJ.', 'error');
        } finally {
            setDescobrindo(null);
        }
    };

    /** Desfaz um CNPJ vinculado errado — mantém os candidatos em cache (sem novo custo). */
    const limparCnpj = async () => {
        if (!modalCandidatos) return;
        const e = modalCandidatos.empresa;
        try {
            const enr = await limparCnpjEscolhido(e.place_id, e.enriquecimento_data || null);
            aplicar(e.place_id, { cnpj: null, enriquecimento_data: enr, enriquecimento_status: 'nao' });
            addToast('CNPJ desvinculado. Reabrir os candidatos não gera novo custo.', 'info');
            setModalCandidatos(null);
        } catch (err: any) {
            addToast(err.message || 'Erro ao desvincular o CNPJ.', 'error');
        }
    };

    const escolherCandidato = async (c: CnpjCandidato) => {
        if (!modalCandidatos) return;
        const e = modalCandidatos.empresa;
        setEscolhendoCnpj(c.cnpj);
        try {
            const con = contatosDoCandidato(c);
            const enrBase: EnriquecimentoData = {
                ...(e.enriquecimento_data || {}),
                ...(con.melhor_whatsapp && !e.enriquecimento_data?.melhor_whatsapp ? { melhor_whatsapp: con.melhor_whatsapp } : {}),
                ...(con.melhor_email && !e.enriquecimento_data?.melhor_email ? { melhor_email: con.melhor_email } : {}),
            };
            const enr = await confirmarCnpjManual(e.place_id, c, enrBase);
            aplicar(e.place_id, { cnpj: c.cnpj, enriquecimento_data: enr });
            addToast(`CNPJ vinculado: ${c.razao_social} · ${maskCnpj(c.cnpj)}`, 'success');
            setModalCandidatos(null);
        } catch (err: any) {
            addToast(err.message || 'Erro ao vincular o CNPJ.', 'error');
        } finally {
            setEscolhendoCnpj(null);
        }
    };

    /** Enriquece via Lemit (empresa → sócios) reaproveitando o pipeline da Lista Fria. */
    const enriquecer = async (e: EmpresaResult) => {
        if (!e.cnpj) return;
        setEnriquecendo(e.place_id);
        try {
            // A linha da Lista Fria está enxuta na tela: o patch sairia sem os sócios gravados
            const base = ehDaListaFria(e) ? (await carregarStagedPorPlaceIds([e.place_id]))[0] : rowFromEmpresa(e);
            if (!base) throw new Error('Lead não encontrado na Lista Fria.');
            const afterEmpresa = await enriquecerEmpresaRow(base);
            const afterSocios = await enriquecerSociosRow(afterEmpresa);
            aplicar(e.place_id, {
                enriquecimento_status: afterSocios.enriquecimento_status,
                enriquecimento_data: afterSocios.enriquecimento_data,
                telefone: afterSocios.telefone || e.telefone,
            });
            addToast(`Enriquecimento concluído para "${e.nome}".`, 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'prospeccao', acao: 'enriquecer_lead_lemit', origem: 'acao' });
        } catch (err: any) {
            aplicar(e.place_id, { enriquecimento_status: 'falhou' });
            addToast(err.message || 'Erro ao enriquecer via Lemit.', 'error');
        } finally {
            setEnriquecendo(null);
        }
    };

    const loteSelecionado = () =>
        resultados.filter(e => selPlaces.includes(e.place_id) && e.status !== 'importado' && !e.in_crm);

    /** Distribui as empresas do lote round-robin entre os responsáveis escolhidos. */
    const distribuirLote = (lote: EmpresaResult[], alvos: string[]): Record<string, EmpresaResult[]> => {
        const buckets: Record<string, EmpresaResult[]> = {};
        alvos.forEach(a => { buckets[a] = []; });
        lote.forEach((e, i) => buckets[alvos[i % alvos.length]].push(e));
        return buckets;
    };

    /** Importação simples em lote, sem rota — distribuída entre os responsáveis selecionados. */
    const importarLote = async () => {
        const lote = loteSelecionado();
        if (lote.length === 0) return;
        setImportandoLote(true);
        const alvos = destinos();
        let ok = 0;
        for (let i = 0; i < lote.length; i++) {
            const negId = await importarCore(lote[i], alvos[i % alvos.length]);
            if (negId) ok++;
        }
        setImportandoLote(false);
        setSelPlaces([]);
        setShowRotaConfig(false);
        addToast(`${ok}/${lote.length} leads importados para ${destNome} em ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} 🎉`, ok === lote.length ? 'success' : 'error');
        logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'prospeccao', acao: 'importar_leads_prospeccao', origem: 'acao' });
    };

    /**
     * Importa os leads e agenda a visita (tarefa) de cada um na data escolhida — distribuída
     * entre os responsáveis. A rota vira artefato do dia: nasce em Rotas → Agenda pelo botão
     * "Criar Rota Inteligente" (spec Bruco — unificação). Não cria mais rota aqui.
     */
    const agendarVisitas = async (config: RotaConfig) => {
        if (!profile) return;
        const lote = loteSelecionado();
        if (lote.length === 0) return;
        setImportandoLote(true);
        const alvos = destinos();
        const buckets = distribuirLote(lote, alvos);
        try {
            let visitasTotal = 0;
            for (const responsavelId of alvos) {
                const grupo = buckets[responsavelId] || [];
                if (grupo.length === 0) continue;
                for (const empresa of grupo) {
                    const negId = await importarCore(empresa, responsavelId);
                    if (!negId) continue;
                    const { error } = await supabase.from('tarefas').insert([{
                        titulo: `Visita — ${empresa.nome}`,
                        descricao: config.observacoes || null,
                        responsavel_id: responsavelId,
                        solicitante_id: profile.id,
                        prioridade: config.prioridade,
                        vencimento: config.data,
                        status: 'a_iniciar',
                        negociacao_id: negId,
                        tipo: 'prospeccao',
                        modalidade: 'presencial',
                        created_at: new Date().toISOString(),
                    }]);
                    if (!error) visitasTotal++;
                }
            }
            if (visitasTotal === 0) throw new Error('Nenhum lead pôde ser importado.');

            setSelPlaces([]);
            setShowRotaConfig(false);
            const quando = new Date(config.data + 'T12:00:00').toLocaleDateString('pt-BR');
            addToast(
                `${visitasTotal} visita(s) agendada(s) para ${destNome} em ${quando}. No dia, crie a rota em Rotas → Agenda 🗓️`,
                'success'
            );
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'prospeccao', acao: 'agendar_visitas_prospeccao', origem: 'acao' });
        } catch (err) {
            console.error('Erro ao agendar visitas da prospecção:', err);
            addToast('Erro ao agendar as visitas. Verifique e tente novamente.', 'error');
        } finally {
            setImportandoLote(false);
        }
    };

    /**
     * Calcula a prévia da rota e joga as empresas consideradas em "Empresas Encontradas".
     *
     * Buscar e rotear eram duas populações diferentes na tela — a coluna mostrava o
     * resultado do Google e o painel falava do pool inteiro no raio, então os números
     * não batiam (o "334 x 85"). Agora a prévia é a fonte da lista: o que está na coluna
     * é exatamente o que entrou na conta da rota.
     */
    const verPreviaRota = async (quantasVisitar: number) => {
        if (!origem) { addToast('Defina o ponto de partida no mapa ou pelo endereço.', 'error'); return; }
        setCalculandoRota(true);
        setPreviaRota(null);
        try {
            // As empresas que estão na tela viram os pontos da rota. Sem isso a edge function
            // ia buscar no pool por conta própria e devolvia leads de buscas antigas — foi o
            // que fez a prévia listar "Bar do véi, Bar Mendes…" com a lista vazia do lado.
            //
            // Só valem se forem DA BASE selecionada: resultado do Google não serve para a
            // Lista Fria (não tem CNPJ para conferir) e vice-versa. Quando não bate, manda
            // vazio e deixa a função consultar o pool da base certa.
            const naTela = resultadosBase !== baseLeads ? [] : resultadosView
                .filter(e => e.latitude != null && e.longitude != null && e.status !== 'importado' && !e.in_crm);

            // Checagem de CNPJ ativo: a função grava `{...enriquecimento_data, cnpjbiz}` no
            // lead que ela conferir. Sem o JSON nos pontos ela gravava `{cnpjbiz}` sozinho
            // por cima do enriquecimento inteiro — 366 leads perderam sócios e CNAE assim — e,
            // sem o cache, pagava o CNPJ.BIZ de novo a cada rota.
            // Situação já conhecida vai como cache (sem crédito, sem gravação); para os
            // poucos sem situação, vai o JSON completo, que a função pode regravar sem perda.
            const checaAtivas = baseLeads === 'lista_fria' && somenteAtivas;
            const semSituacao = checaAtivas ? naTela.filter(e => ehDaListaFria(e) && !e.situacao).map(e => e.place_id) : [];
            const completos = new Map<string, EnriquecimentoData | null>();
            if (semSituacao.length > 0) {
                (await carregarStagedPorPlaceIds(semSituacao)).forEach(r => completos.set(r.place_id, r.enriquecimento_data));
            }
            const enrDoPonto = (e: EmpresaResult) => {
                if (!checaAtivas || !ehDaListaFria(e)) return null;
                if (e.situacao) return { cnpjbiz: { situacao: e.situacao } };
                return completos.get(e.place_id) ?? undefined;
            };

            const pontos = naTela
                // Sem o JSON completo não dá para mandar com segurança: fica de fora da conta
                .filter(e => enrDoPonto(e) !== undefined)
                .map(e => ({
                    id: (e as any).id ?? e.place_id,
                    place_id: e.place_id,
                    nome: e.nome,
                    cnpj: (e as any).cnpj ?? null,
                    categoria: e.categoria ?? null,
                    endereco: (e as any).endereco ?? null,
                    cidade: (e as any).cidade ?? null,
                    bairro: (e as any).bairro ?? null,
                    latitude: e.latitude,
                    longitude: e.longitude,
                    kwh_estimado: e.kwh_estimado ?? null,
                    enriquecimento_data: enrDoPonto(e),
                }));

            const { data, error } = await supabase.functions.invoke('rota-otima', {
                body: {
                    base: baseLeads,
                    origem: { lat: origem.lat, lng: origem.lng },
                    // null = rota aberta (termina na última parada)
                    destino: destino ? { lat: destino.lat, lng: destino.lng } : null,
                    quantidade: quantasVisitar,
                    raioKm: raio,
                    // Prioridade é tempo puro: potencial e espalhamento ficam fora da conta
                    pesoKwh: 0,
                    distanciaMinKm: 0,
                    somenteAtivas: baseLeads === 'lista_fria' ? somenteAtivas : false,
                    cidade: cidadeSel.split('|')[0] || null,
                    categorias: categoriasSel,
                    // Vazio = não há busca na tela (Lista Fria); aí a função consulta o pool.
                    pontos,
                },
            });
            if (error) {
                // O SDK engole a causa em resposta não-2xx; o motivo real está no context
                const detalhe = await (error as any)?.context?.json?.().catch(() => null);
                throw new Error(detalhe?.message || detalhe?.error || 'Erro ao calcular a rota.');
            }
            // Só substitui a lista quando as empresas vieram do pool (Lista Fria). Se a rota
            // foi montada sobre o que já estava na tela, manter o que está lá preserva
            // rating/in_crm/seleção que a busca do Google trouxe e a função não devolve.
            const empresas = (data?.empresas || []) as EmpresaResult[];
            if (pontos.length === 0 && empresas.length > 0) { setResultados(empresas); setResultadosBase(baseLeads); }
            setBuscou(true);
            if (!data?.paradas?.length) {
                addToast(data?.message || 'Nenhuma empresa encontrada com esses filtros.', 'info');
                return;
            }
            setPreviaRota(data as PreviaRota);
            const fora = Number(data?.no_raio || 0) - empresas.length;
            addToast(
                `${data.paradas.length} paradas · ${data.distancia_km} km` +
                (data.duracao_min ? ` · ~${data.duracao_min} min` : '') +
                (fora > 0 ? ` (${fora} fora dos segmentos)` : ''),
                'success',
            );
        } catch (err: any) {
            console.error('Erro na prévia da rota:', err);
            addToast(err.message || 'Erro ao calcular a rota.', 'error');
        } finally {
            setCalculandoRota(false);
        }
    };

    /**
     * Confirmação da prévia: importa os leads, agenda a visita de cada um e grava a rota
     * NA ORDEM APROVADA. Não passa pelo gerarRotaInteligente de propósito — ele reotimiza,
     * e a sequência gravada tem que ser a mesma que apareceu na tela.
     */
    const confirmarRotaOtima = async (previa: PreviaRota) => {
        if (!profile || !firstStage) { addToast('Funil de vendas não carregado.', 'error'); return; }
        const responsavelId = vendedoresImport[0] || profile.id;
        const dataRota = dataRotaSel;
        setCriandoRota(true);
        try {
            // A ROTA VEM PRIMEIRO — de propósito.
            //
            // Existe um índice único (vendedor_id, data) para rota inteligente planejada, e
            // antes a rota era o último insert: os leads e as tarefas já tinham sido gravados
            // quando a colisão estourava, e ficavam órfãos. Três cliques no botão deixaram 30
            // negociações e 30 tarefas sem rota, com as empresas marcadas como "importado" no
            // pool. Criando a rota antes, a colisão falha com o banco ainda intacto.
            let rotaId: string;
            let posicaoInicial = 0;

            const { data: existente } = await supabase
                .from('rotas_comerciais')
                .select('id')
                .eq('vendedor_id', responsavelId)
                .eq('data', dataRota)
                .eq('origem', 'inteligente')
                .eq('status', 'planejada')
                .maybeSingle();

            if (existente) {
                // Já há rota inteligente nesse dia: as paradas entram no fim dela em vez de
                // estourar a constraint (o usuário vê o aviso e pode trocar a data).
                rotaId = existente.id;
                const { count } = await supabase
                    .from('rota_paradas')
                    .select('id', { count: 'exact', head: true })
                    .eq('rota_id', rotaId);
                posicaoInicial = count || 0;
                // Somar paradas a uma rota existente não pode descartar a chegada escolhida
                // agora — senão o Maps continuaria terminando na última empresa.
                if (destino) {
                    await supabase.from('rotas_comerciais')
                        .update({ destino_lat: destino.lat, destino_lng: destino.lng, destino_label: destino.label })
                        .eq('id', rotaId);
                }
            } else {
                const { data: rota, error: errRota } = await supabase.from('rotas_comerciais').insert([{
                    vendedor_id: responsavelId,
                    data: dataRota,
                    origem: 'inteligente',
                    status: 'planejada',
                    distancia_total_km: previa.distancia_km,
                    duracao_min: previa.duracao_min,
                    gerada_em: new Date().toISOString(),
                    // Persiste a chegada: quem abre a rota no celular é o operador, horas
                    // depois, e o link de navegação precisa reproduzir o mesmo destino.
                    destino_lat: destino?.lat ?? null,
                    destino_lng: destino?.lng ?? null,
                    destino_label: destino?.label ?? null,
                }]).select('id').single();
                if (errRota || !rota) throw errRota || new Error('Falha ao criar a rota.');
                rotaId = rota.id;
            }

            const paradas: Array<{ negociacao_id: number; tarefa_id: number }> = [];
            const porPlace = new Map(resultados.map(e => [e.place_id, e]));
            for (const p of previa.paradas) {
                // Mesmo caminho do botão "Importar": a parada montava o lead à mão, sem
                // WhatsApp, e-mail nem representante, com a natureza jurídica como segmento.
                const empresa: EmpresaResult = porPlace.get(p.place_id || '') || {
                    ...(p as any), place_id: p.place_id || `pool:${p.id}`, status: 'disponivel', in_crm: false,
                };
                const negId = await importarCore(empresa, responsavelId);
                if (!negId) { console.error('Erro ao importar lead da rota:', p.nome); continue; }
                const neg = { id: negId };

                const { data: tarefa } = await supabase.from('tarefas').insert([{
                    titulo: `Visita — ${p.nome}`,
                    responsavel_id: responsavelId,
                    solicitante_id: profile.id,
                    prioridade: 'media',
                    vencimento: dataRota,
                    status: 'a_iniciar',
                    negociacao_id: neg.id,
                    tipo: 'visita',
                    modalidade: 'presencial',
                    created_at: new Date().toISOString(),
                }]).select('id').single();
                if (tarefa) paradas.push({ negociacao_id: neg.id, tarefa_id: tarefa.id });
            }
            if (paradas.length === 0) throw new Error('Nenhum lead pôde ser importado.');

            await supabase.from('rota_paradas').insert(
                paradas.map((p, i) => ({
                    rota_id: rotaId, posicao: posicaoInicial + i + 1,
                    negociacao_id: p.negociacao_id, tarefa_id: p.tarefa_id, modalidade: 'presencial',
                }))
            );
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'prospeccao', acao: 'criar_rota_prospeccao', origem: 'acao' });

            const dataBR = new Date(dataRota + 'T12:00:00').toLocaleDateString('pt-BR');
            addToast(
                existente
                    ? `${paradas.length} paradas somadas à rota que já existia em ${dataBR} (agora com ${posicaoInicial + paradas.length}) 🚗`
                    : `Rota criada com ${paradas.length} paradas para ${dataBR}` +
                      `${previa.duracao_min ? ` · ~${previa.duracao_min} min de direção` : ''} 🚗`,
                'success'
            );
            setPreviaRota(null);
        } catch (err: any) {
            console.error('Erro ao confirmar a rota ótima:', err);
            addToast(err.message || 'Erro ao criar a rota.', 'error');
        } finally {
            setCriandoRota(false);
        }
    };

    // Pins em canvas, memorizados: digitar em outro campo não redesenha milhares de pins.
    // `bubblingMouseEvents: false` — sem isso o clique no pin chegava ao mapa e movia a origem.
    const pinsResultados = useMemo(() => resultadosView
        .filter(e => e.latitude != null && e.longitude != null)
        .map(e => {
            const dest = destacado === e.place_id;
            return (
                <CircleMarker
                    key={e.place_id}
                    center={[e.latitude!, e.longitude!]}
                    radius={dest ? 9 : 6}
                    bubblingMouseEvents={false}
                    pathOptions={{ color: dest ? '#1d4ed8' : '#fff', weight: dest ? 3 : 1.5, fillColor: PIN_COLOR[statusPin(e)], fillOpacity: 0.95 }}
                    eventHandlers={{ click: () => { setDestacado(e.place_id); setPopupPlace(e.place_id); } }}
                />
            );
        }), [resultadosView, selPlaces, destacado]);
    const empresaPopup = popupPlace ? resultados.find(e => e.place_id === popupPlace) || null : null;

    // Tela restrita: apenas adm e gerente (a edge function também valida no servidor)
    if (!isAllowed) {
        return (
            <div className="flex flex-col items-center justify-center py-24 text-center px-4">
                <ShieldAlert size={44} className="text-gray-300 dark:text-gray-600 mb-4" />
                <h2 className="text-base font-bold text-gray-700 dark:text-gray-200">Acesso restrito</h2>
                <p className="text-sm text-gray-400 mt-1 max-w-sm">A Prospecção Geográfica consome API paga e está disponível apenas para administradores e gerentes comerciais.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4 px-2 sm:px-0">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-xl">
                        <Search size={22} />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Prospecção Geográfica</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Encontre empresas com alto potencial de consumo, ainda fora do CRM</p>
                    </div>
                </div>
                <button
                    onClick={() => setShowUsage(true)}
                    className="flex items-center gap-2 px-3.5 py-2 text-xs font-bold rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors border border-emerald-200 dark:border-emerald-800"
                >
                    <DollarSign size={14} /> Registro de Uso da API
                </button>
            </div>

            {missingKey && (
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                        <p className="font-bold mb-0.5">Chave do Google Places não configurada</p>
                        <p>Para usar a prospecção, crie uma chave da <b>Places API (New)</b> no Google Cloud e salve em <b>system_settings</b> (chave <code>google_places_api_key</code>). Os resultados ficam em cache permanente — cada busca só é paga uma vez.</p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Painel de busca */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 h-fit">
                    {/* Base de leads: decide de onde saem as empresas — e, por consequência,
                        quais filtros fazem sentido daqui para baixo. */}
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Base de leads</label>
                    <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                        <button
                            // Voltar para o Google limpa o que veio da Lista Fria: lá os
                            // resultados aparecem sozinhos, aqui só depois de buscar (e pagar).
                            onClick={() => {
                                setBaseLeads('google');
                                if (resultadosBase === 'lista_fria') {
                                    setResultados([]); setResultadosBase(null); setBuscou(false); setPreviaRota(null);
                                }
                            }}
                            className={`px-2.5 py-2 rounded-xl text-xs font-bold border transition-all ${baseLeads === 'google'
                                ? 'bg-primary/10 border-primary/40 text-primary'
                                : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
                        >
                            🌐 Google
                        </button>
                        <button
                            // A Lista Fria não tem kWh estimado: ordenar por ele embaralhava a lista
                            onClick={() => { setBaseLeads('lista_fria'); setSortBy('distancia'); }}
                            className={`px-2.5 py-2 rounded-xl text-xs font-bold border transition-all ${baseLeads === 'lista_fria'
                                ? 'bg-primary/10 border-primary/40 text-primary'
                                : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
                        >
                            🗂️ Lista Fria (CNPJ)
                        </button>
                    </div>
                    <p className="text-[10px] text-gray-400 mb-2">
                        {baseLeads === 'google'
                            ? 'Busca no Google Places: mais cobertura, mas sem CNPJ — não dá para saber se a empresa ainda está ativa.'
                            : 'Usa os CNPJs já carregados na Lista Fria: dá para conferir a situação cadastral antes de mandar alguém na porta.'}
                    </p>
                    {/* Só faz sentido na Lista Fria — o Google não devolve CNPJ para conferir. */}
                    {baseLeads === 'lista_fria' && (
                        <label className="flex items-start gap-2 mb-4 text-[11px] font-semibold text-gray-600 dark:text-gray-300 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={somenteAtivas}
                                onChange={e => setSomenteAtivas(e.target.checked)}
                                className="mt-0.5 rounded text-primary focus:ring-primary shrink-0"
                            />
                            <span>
                                Só empresas com CNPJ ativo
                                <span className="block text-gray-400 font-normal">
                                    Esconde da lista, do mapa e da rota os CNPJs inaptos, baixados ou suspensos. Usa a situação que a Lemit já trouxe — sem custo.
                                </span>
                            </span>
                        </label>
                    )}
                    {baseLeads === 'google' && <div className="mb-4" />}

                    <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Região <span className="text-gray-400 font-normal">(contexto)</span></label>
                    <SearchableSelect
                        value={cidadeSel}
                        onChange={setCidadeSel}
                        options={cidades.map(c => ({
                            value: `${c.cidade}|${c.estado}`,
                            label: `${c.cidade}${c.estado ? ` - ${c.estado}` : ''}`,
                        }))}
                        allLabel="Selecione a cidade..."
                        placeholder="Buscar cidade…"
                        className="w-full mb-4"
                    />

                    {/* Ponto de origem da busca — independente da Região (endereço / pin / minha localização) */}
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Ponto de origem da busca</label>
                    <div className="relative mb-1.5">
                        <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 pointer-events-none" />
                        <input
                            value={enderecoInput}
                            onChange={e => { setEnderecoInput(e.target.value); setEnderecoBusca(e.target.value); }}
                            placeholder="Digite um endereço..."
                            className="w-full pl-9 pr-8 py-2 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                        {(enderecoInput || origem) && (
                            <button onClick={limparOrigem} title="Limpar origem (voltar ao centro da região)" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                <X size={14} />
                            </button>
                        )}
                        {(buscandoEndereco || sugestoes.length > 0) && enderecoBusca.trim().length >= 3 && (
                            <div className="absolute z-[1200] mt-1 left-0 right-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-xl overflow-hidden max-h-56 overflow-y-auto">
                                {buscandoEndereco && sugestoes.length === 0 ? (
                                    <p className="px-3 py-2 text-xs text-gray-400">Buscando endereços...</p>
                                ) : sugestoes.map(s => (
                                    <button
                                        key={s.placeId}
                                        onClick={() => escolherSugestao(s)}
                                        className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-primary/5 flex items-start gap-2"
                                    >
                                        <MapPin size={12} className="text-blue-500 shrink-0 mt-0.5" /> <span>{s.text}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={usarMinhaLocalizacao}
                        disabled={localizando}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 mb-1.5 text-xs font-bold rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors disabled:opacity-50"
                    >
                        <Crosshair size={13} /> {localizando ? 'Localizando...' : 'Usar minha localização'}
                    </button>
                    <p className="text-[10px] text-gray-400 mb-3 flex items-center gap-1">
                        <LocateFixed size={11} className="text-blue-500 shrink-0" />
                        {origem
                            ? <>Origem: <b className="text-gray-600 dark:text-gray-300 truncate">{origem.label}</b></>
                            : 'Sem ponto custom — usando o centro da região. Você também pode clicar/arrastar o pin no mapa.'}
                    </p>

                    {/* Ponto de chegada — opcional. Sem ele a rota termina na última parada. */}
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                        Ponto de chegada <span className="text-gray-400 font-normal">(opcional)</span>
                    </label>
                    <div className="relative mb-1.5">
                        <Flag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500 pointer-events-none" />
                        <input
                            value={destinoInput}
                            onChange={e => { setDestinoInput(e.target.value); setDestinoBusca(e.target.value); }}
                            placeholder="Onde a rota termina..."
                            className="w-full pl-9 pr-8 py-2 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
                        />
                        {(destinoInput || destino) && (
                            <button
                                onClick={() => { setDestino(null); setDestinoInput(''); setDestinoBusca(''); setSugestoesDestino([]); }}
                                title="Remover ponto de chegada"
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <X size={14} />
                            </button>
                        )}
                        {sugestoesDestino.length > 0 && destinoBusca.trim().length >= 3 && (
                            <div className="absolute z-[1200] mt-1 left-0 right-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-xl overflow-hidden max-h-56 overflow-y-auto">
                                {sugestoesDestino.map(s => (
                                    <button
                                        key={s.placeId}
                                        onClick={() => escolherDestino(s)}
                                        className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-emerald-500/5 flex items-start gap-2"
                                    >
                                        <Flag size={12} className="text-emerald-500 shrink-0 mt-0.5" /> <span>{s.text}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <p className="text-[10px] text-gray-400 mb-3">
                        {destino
                            ? <>Chegada: <b className="text-gray-600 dark:text-gray-300">{destino.label}</b> — as paradas são escolhidas pelo menor desvio no caminho.</>
                            : 'Sem chegada definida, a rota termina na última parada.'}
                    </p>

                    <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                        {destino ? <>Desvio máximo: {raio} km <span className="text-gray-400 font-normal">(quanto pode sair do trajeto)</span></> : <>Raio: {raio} km <span className="text-gray-400 font-normal">(máx. 50 km)</span></>}
                    </label>
                    <input
                        type="range" min={1} max={50} value={raio}
                        onChange={e => setRaio(Number(e.target.value))}
                        className="w-full accent-primary mb-4"
                    />

                    <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                        Categorias <span className="text-gray-400 font-normal">({categoriasSel.length} selecionada{categoriasSel.length !== 1 ? 's' : ''} — sem limite)</span>
                    </label>
                    <div className="max-h-[320px] overflow-y-auto pr-1 mb-2">
                        {GRUPOS_CATEGORIAS.map(g => (
                            <div key={g.grupo} className="mb-2">
                                <p className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">{g.grupo}</p>
                                <div className="grid grid-cols-2 gap-1.5">
                                    {g.itens.map(c => {
                                        const active = categoriasSel.includes(c.categoria);
                                        return (
                                            <button
                                                key={c.categoria}
                                                onClick={() => toggleCategoria(c.categoria)}
                                                className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-semibold border transition-all text-left ${active
                                                    ? 'bg-primary/10 border-primary/40 text-primary'
                                                    : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'
                                                    }`}
                                            >
                                                <span>{c.emoji}</span> {c.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Só o Google tem limite por categoria e custo por chamada; na Lista Fria
                        quantas entram na rota é perguntado ao gerar a rota. */}
                    {baseLeads === 'google' && (<>
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5 mt-1">
                        Resultados por categoria
                        <span className="text-gray-400 font-normal"> ({maxPorCategoria >= 20 ? 'todos — máx. 20' : `até ${maxPorCategoria}`})</span>
                    </label>
                    <div className="flex items-center gap-1.5 mb-3">
                        {[5, 10, 15, 20].map(n => (
                            <button
                                key={n}
                                onClick={() => setMaxPorCategoria(n)}
                                className={`flex-1 px-2 py-1.5 rounded-xl text-xs font-bold border transition-all ${maxPorCategoria === n
                                    ? 'bg-primary/10 border-primary/40 text-primary'
                                    : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
                            >
                                {n === 20 ? 'Todos' : n}
                            </button>
                        ))}
                    </div>
                    <p className="text-[10px] text-gray-400 mb-1">🔢 Traz só os mais próximos do centro, até a quantidade escolhida por categoria.</p>
                    <p className="text-[10px] text-gray-400 mb-4">💰 Cada categoria selecionada = 1 chamada à API (~US$ 0,032), independente da quantidade. Tudo fica registrado.</p>
                    </>)}

                    {/* Duas ações distintas de propósito: BUSCAR traz empresas novas (paga a API
                        do Google); a PRÉVIA só ordena o que já temos e não grava nada. */}
                    {baseLeads === 'google' ? (
                        <button
                            onClick={buscar}
                            disabled={buscando || !centroEfetivo || categoriasSel.length === 0}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                        >
                            <Search size={15} /> {buscando ? 'Buscando...' : 'Buscar Empresas'}
                        </button>
                    ) : (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 bg-emerald-50/60 dark:bg-emerald-900/15 rounded-xl px-3 py-2.5">
                            Sem busca paga aqui: os leads do raio já aparecem no mapa conforme você marca as
                            categorias.{' '}
                            {carregandoPool
                                ? <b className="text-gray-500 dark:text-gray-300">Carregando a Lista Fria…</b>
                                : buscou && resultadosBase === 'lista_fria' && (
                                    <b className="text-emerald-700 dark:text-emerald-400">
                                        {resultadosVisiveis.length} no raio de {raio} km
                                        {inativasOcultas > 0 && <span className="font-normal text-gray-500 dark:text-gray-400"> · {inativasOcultas} com CNPJ inativo ocultas</span>}.
                                    </b>
                                )}
                        </p>
                    )}

                    <button
                        onClick={() => {
                            const disponiveis = resultadosView.filter(e => e.latitude != null && e.status !== 'importado' && !e.in_crm).length;
                            setQtdVisitar(String(Math.min(disponiveis || 10, 20)));
                            setPerguntandoQtd(true);
                        }}
                        disabled={calculandoRota || !origem}
                        className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl border-2 border-primary/40 text-primary hover:bg-primary/5 transition-colors disabled:opacity-40"
                    >
                        <RouteIcon size={15} /> {calculandoRota ? 'Calculando...' : 'Gerar rota otimizada'}
                    </button>
                    <p className="text-[10px] text-gray-400 mt-1">
                        {!origem
                            ? 'Defina o ponto de partida no mapa ou pelo endereço para montar a rota.'
                            : buscou && resultados.length > 0
                                ? <>Ordena as <b className="text-gray-500 dark:text-gray-300">{resultadosView.length}</b> empresas da lista pelo menor tempo a partir de <b className="text-gray-500 dark:text-gray-300">{origem.label}</b>. Nada é gravado até você confirmar.</>
                                : 'Busque as empresas primeiro — a rota é montada em cima do que aparecer na lista.'}
                    </p>

                    {/* Histórico de buscas salvas — reabrir não consome API */}
                    {historico.length > 0 && (
                        <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700">
                            <p className="text-xs font-bold text-gray-700 dark:text-gray-200 flex items-center gap-1.5 mb-0.5">
                                <History size={13} className="text-gray-400" /> Histórico de Buscas
                            </p>
                            <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mb-2">Reabrir uma busca salva é grátis — não consome a API.</p>
                            <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
                                {historico.map(h => (
                                    <button
                                        key={h.id}
                                        onClick={() => abrirBusca(h)}
                                        disabled={abrindoBusca === h.id}
                                        className="w-full text-left p-2.5 rounded-xl bg-gray-50/80 dark:bg-gray-700/30 border border-transparent hover:border-primary/30 hover:bg-primary/5 transition-all disabled:opacity-50"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">
                                                {h.cidade || 'Região'}{h.estado ? `-${h.estado}` : ''} · {h.raio_km || '?'} km
                                            </p>
                                            <span className="text-[10px] text-gray-400 shrink-0">
                                                {new Date(h.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} {new Date(h.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{(h.categorias || []).join(', ')}</p>
                                        <p className="text-[10px] text-gray-400 mt-0.5">
                                            <span className="font-bold text-gray-600 dark:text-gray-300">{h.resultados}</span> resultados · {profiles.find(p => String(p.id) === String(h.user_id))?.full_name?.split(' ')[0] || '—'}
                                            {abrindoBusca === h.id ? ' · abrindo...' : ''}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Quantas das empresas encontradas entram na rota */}
                {perguntandoQtd && (() => {
                    const disponiveis = resultadosView.filter(e => e.latitude != null && e.status !== 'importado' && !e.in_crm).length;
                    const n = Math.max(1, Math.min(Number(qtdVisitar) || 1, disponiveis || 1));
                    return createPortal(
                        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50" onClick={() => setPerguntandoQtd(false)}>
                            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center gap-2.5">
                                    <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0"><RouteIcon size={18} /></div>
                                    <div className="min-w-0">
                                        <h2 className="text-sm font-bold text-gray-900 dark:text-white">Quantas empresas visitar?</h2>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                            {disponiveis} disponíveis na lista · partindo de {origem?.label}
                                        </p>
                                    </div>
                                </div>
                                <input
                                    type="number" min={1} max={disponiveis || 1} value={qtdVisitar}
                                    onChange={e => setQtdVisitar(e.target.value)}
                                    autoFocus
                                    className="w-full px-3 py-2.5 text-lg font-bold text-center rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                />
                                <div className="flex flex-wrap gap-1.5">
                                    {[5, 10, 15, 20, 30].filter(v => v <= (disponiveis || 0)).map(v => (
                                        <button key={v} onClick={() => setQtdVisitar(String(v))}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${Number(qtdVisitar) === v
                                                ? 'bg-primary/10 border-primary/40 text-primary'
                                                : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                                            {v}
                                        </button>
                                    ))}
                                    {disponiveis > 0 && (
                                        <button onClick={() => setQtdVisitar(String(disponiveis))}
                                            className="px-3 py-1.5 rounded-lg text-xs font-bold border bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                            Todas ({disponiveis})
                                        </button>
                                    )}
                                </div>
                                {n > 24 && (
                                    <p className="text-[10px] text-amber-600 dark:text-amber-400">
                                        Acima de 24 paradas o Google não otimiza a ordem — a sequência sai por cálculo local (2-opt), ainda coerente mas estimada.
                                    </p>
                                )}
                                <button
                                    onClick={() => { setPerguntandoQtd(false); verPreviaRota(n); }}
                                    disabled={disponiveis === 0}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 disabled:opacity-40"
                                >
                                    <RouteIcon size={15} /> Montar rota com {n}
                                </button>
                            </div>
                        </div>,
                        document.body,
                    );
                })()}

                {/* Resultados */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Prévia da rota — some junto com os resultados; nada foi gravado ainda */}
                    {/* A sequência das paradas já está numerada na lista e no mapa abaixo —
                        repetir "1. Bar do véi → 2. …" aqui era ruído. No momento de criar, a
                        informação que falta é PARA QUEM a rota vai. */}
                    {previaRota && (
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-primary/30 shadow-sm p-4">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                <span className="text-xs font-bold text-gray-900 dark:text-white">
                                    Rota de {previaRota.paradas.length} paradas
                                    <span className="font-normal text-gray-400">
                                        {' · '}{previaRota.distancia_km.toLocaleString('pt-BR')} km
                                        {previaRota.duracao_min != null && ` · ~${previaRota.duracao_min} min`}
                                    </span>
                                </span>
                                <button
                                    onClick={() => setPreviaRota(null)}
                                    className="text-[11px] font-semibold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    Descartar
                                </button>
                            </div>

                            <label className="text-[11px] font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                                Dia da rota
                            </label>
                            <input
                                type="date"
                                value={dataRotaSel}
                                min={new Date().toISOString().slice(0, 10)}
                                onChange={e => setDataRotaSel(e.target.value)}
                                className="w-full mb-3 px-3 py-2 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />

                            <label className="text-[11px] font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                                Importar esta rota para
                            </label>
                            <div className="flex items-center gap-2 mb-3">
                                <ResponsibleFilter
                                    selectedResponsibles={vendedoresImport}
                                    setSelectedResponsibles={setVendedoresImport}
                                    profiles={profiles}
                                    currentUserProfile={profile}
                                    label="Mim"
                                    allLabel="Mim"
                                    className="shrink-0"
                                />
                                <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                    {vendedoresImport.length > 1
                                        ? `${vendedoresImport.length} vendedores — a rota fica com ${nomeCurto(vendedoresImport[0])}`
                                        : <>Os {previaRota.paradas.length} leads e as visitas ficam com <b className="text-gray-700 dark:text-gray-200">{destNome}</b></>}
                                </span>
                            </div>

                            <button
                                onClick={() => confirmarRotaOtima(previaRota)}
                                disabled={criandoRota}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl bg-emerald-500 text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                            >
                                <RouteIcon size={15} /> {criandoRota ? 'Criando...' : `Criar rota para ${destNome}`}
                            </button>
                        </div>
                    )}
                    {/* Mapa dos resultados */}
                    <div className={`${mapaExpandido ? 'h-[72vh]' : 'h-[280px]'} rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-inner relative z-0 transition-[height] duration-300`}>
                        <MapContainer center={centroEfetivo || [-16.68, -49.25]} zoom={11} scrollWheelZoom preferCanvas className="h-full w-full">
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <MapAutoResize />
                            <FlyTo target={centroEfetivo} />
                            <FlyTo target={flyResult} />
                            <MapClickHandler onPick={definirOrigemPorMapa} />
                            <BoundsWatcher onBounds={setMapBounds} />
                            {/* Círculo do raio desenhado a partir do ponto de origem */}
                            {centroEfetivo && (
                                <Circle
                                    center={centroEfetivo}
                                    radius={raio * 1000}
                                    pathOptions={{ color: '#2563eb', weight: 1.5, fillColor: '#2563eb', fillOpacity: 0.06 }}
                                />
                            )}
                            {/* Pin de origem arrastável (distinto dos resultados) */}
                            {centroEfetivo && (
                                <Marker
                                    position={centroEfetivo}
                                    icon={pinOrigem}
                                    draggable
                                    eventHandlers={{ dragend: (e) => { const ll = (e.target as L.Marker).getLatLng(); definirOrigemPorMapa(ll.lat, ll.lng); } }}
                                >
                                    <Popup>
                                        <p className="font-semibold">Ponto de origem</p>
                                        <p className="text-xs text-gray-600">{origem ? origem.label : 'Centro da região (arraste p/ ajustar)'}</p>
                                    </Popup>
                                </Marker>
                            )}
                            {pinsResultados}
                            {empresaPopup && empresaPopup.latitude != null && empresaPopup.longitude != null && (() => {
                                const e = empresaPopup;
                                return (
                                    <Popup
                                        key={e.place_id}
                                        position={[e.latitude!, e.longitude!]}
                                        offset={[0, -4]}
                                        eventHandlers={{ remove: () => setPopupPlace(p => (p === e.place_id ? null : p)) }}
                                    >
                                        <p className="font-semibold">{e.nome}</p>
                                        <p className="text-xs text-gray-600">{e.categoria}{e.kwh_estimado ? ` · ${Math.round(e.kwh_estimado).toLocaleString('pt-BR')} kWh/mês est.` : ''}</p>
                                        {e.situacao && e.situacao !== 'ATIVA' && (
                                            <p className="text-xs font-semibold text-red-600 mt-0.5">CNPJ {e.situacao}</p>
                                        )}
                                        {e.status === 'importado' ? (
                                            <p className="text-xs font-semibold text-gray-500 mt-1">Já importado</p>
                                        ) : e.in_crm ? (
                                            <p className="text-xs font-semibold text-gray-500 mt-1">Já no CRM</p>
                                        ) : (
                                            <button
                                                onClick={() => importar(e)}
                                                disabled={importando === e.place_id || !firstStage}
                                                className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded bg-primary text-white hover:opacity-90 disabled:opacity-40"
                                            >
                                                <Download size={11} /> {importando === e.place_id ? 'Importando...' : 'Importar'}
                                            </button>
                                        )}
                                    </Popup>
                                );
                            })()}
                        </MapContainer>

                        {/* Controles overlay: expandir + selecionar área */}
                        <div className="absolute top-2 right-2 z-[500] flex flex-col items-end gap-1.5">
                            <button onClick={() => setMapaExpandido(v => !v)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur text-xs font-bold text-gray-700 dark:text-gray-200 shadow hover:bg-white dark:hover:bg-gray-800">
                                {mapaExpandido ? <><Minimize2 size={12} /> Recolher</> : <><Maximize2 size={12} /> Expandir mapa</>}
                            </button>
                            {buscou && resultadosView.some(e => e.status !== 'importado' && !e.in_crm) && (
                                <button onClick={selecionarNaArea} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur text-xs font-bold text-primary shadow hover:bg-white dark:hover:bg-gray-800">
                                    <BoxSelect size={12} /> Selecionar área
                                </button>
                            )}
                        </div>

                        {/* Legenda de status dos pins */}
                        <div className="absolute bottom-2 left-2 z-[500] flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur text-[10px] font-semibold text-gray-600 dark:text-gray-300 shadow">
                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#f97316' }} /> disponível</span>
                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#2563eb' }} /> selecionado</span>
                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#9ca3af' }} /> importado</span>
                        </div>
                    </div>

                    {/* Lista */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                            {/* O contador dizia "85 de 334" sem explicar o corte, e parecia
                                que a tela estava mentindo. Agora o número grande é sempre o
                                que está à vista e o resto vira legenda com o motivo. */}
                            <div>
                                <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                                    {buscou ? `Empresas Encontradas (${resultadosView.length})` : 'Busque empresas com potencial'}
                                </h2>
                                {buscou && resultadosView.length !== resultados.length && (
                                    <p className="text-[10px] text-gray-400">
                                        {resultados.length - resultadosView.length} de {resultados.length} ocultas
                                        {inativasOcultas > 0 && ` · ${inativasOcultas} com CNPJ inativo`}
                                        {kwhMin > 0 && ` · filtro de ${kwhMin.toLocaleString('pt-BR')} kWh`}
                                        {buscaNome.trim() && ` · busca "${buscaNome.trim()}"`}
                                    </p>
                                )}
                            </div>
                            {buscou && resultados.length > 0 && (
                                <div className="flex items-center gap-2 shrink-0">
                                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-gray-100 dark:bg-gray-700" title="Mostra só empresas com potencial estimado a partir deste volume (kWh/mês)">
                                        <Zap size={13} className="text-blue-500" />
                                        <input
                                            type="number"
                                            min={0}
                                            step={1000}
                                            value={kwhMin || ''}
                                            onChange={e => setKwhMin(Math.max(0, Number(e.target.value) || 0))}
                                            placeholder="kWh mín."
                                            className="w-20 bg-transparent text-xs font-semibold text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none"
                                        />
                                        {kwhMin > 0 && (
                                            <button onClick={() => setKwhMin(0)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" title="Limpar filtro">
                                                <X size={12} />
                                            </button>
                                        )}
                                    </div>
                                    <ResponsibleFilter
                                        selectedResponsibles={vendedoresImport}
                                        setSelectedResponsibles={setVendedoresImport}
                                        profiles={profiles}
                                        currentUserProfile={profile}
                                        label="Importar para mim"
                                        allLabel="Importar para mim"
                                        className="shrink-0"
                                        align="right"
                                    />
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                            {restauradaEm
                                ? `Busca salva restaurada (${new Date(restauradaEm).toLocaleDateString('pt-BR')} ${new Date(restauradaEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}) — sem novo custo de API`
                                : !buscou ? 'Selecione a região, o raio e as categorias para encontrar empresas não cadastradas no CRM'
                                    : resultadosBase === 'lista_fria' ? 'CNPJs da Lista Fria ainda não importados, no raio escolhido'
                                    : 'Resultado do Google Places · cache permanente por busca'}
                        </p>

                        {/* Barra de indicadores fixa + busca/ordenação (adendo Bruno) */}
                        {buscou && resultados.length > 0 && (
                            <div className="sticky top-0 z-10 -mx-5 px-5 py-2.5 mb-3 bg-white/95 dark:bg-gray-800/95 backdrop-blur border-b border-gray-100 dark:border-gray-700 space-y-2">
                                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                                    <span className="px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 font-bold text-gray-700 dark:text-gray-200">{resultadosView.length} encontrada(s)</span>
                                    <span className="px-2 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/20 font-bold text-blue-700 dark:text-blue-400">{selPlaces.length} selecionada(s)</span>
                                    <span className="px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 font-bold text-emerald-700 dark:text-emerald-400">
                                        {Math.round(resultadosView.reduce((s, e) => s + Number(e.kwh_estimado || 0), 0)).toLocaleString('pt-BR')} kWh/mês est.
                                    </span>
                                    <div className="relative flex-1 min-w-[140px] ml-auto">
                                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            value={buscaNome}
                                            onChange={e => setBuscaNome(e.target.value)}
                                            placeholder="Buscar por nome…"
                                            className="w-full pl-8 pr-2 py-1.5 text-xs rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        />
                                    </div>
                                    <div className="flex items-center gap-1 text-gray-400"><ArrowUpDown size={13} />
                                        <select value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)}
                                            className="text-xs font-semibold px-2 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200">
                                            <option value="kwh">kWh médio do segmento</option>
                                            <option value="distancia">Distância da origem</option>
                                            <option value="rating">Avaliação</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Barra de seleção múltipla / importação em lote */}
                        {(() => {
                            const importaveis = resultadosView.filter(e => e.status !== 'importado' && !e.in_crm);
                            if (importaveis.length === 0) return null;
                            const todosSel = importaveis.length > 0 && importaveis.every(e => selPlaces.includes(e.place_id));
                            return (
                                <div className="flex flex-wrap items-center gap-2 mb-3 p-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700">
                                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={todosSel}
                                            onChange={() => setSelPlaces(todosSel ? [] : importaveis.map(e => e.place_id))}
                                            className="rounded text-primary focus:ring-primary"
                                        />
                                        Selecionar Todos ({importaveis.length})
                                    </label>
                                    {selPlaces.length > 0 && (
                                        <button
                                            onClick={() => setShowRotaConfig(true)}
                                            disabled={importandoLote || !firstStage}
                                            className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-full bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40 ml-auto"
                                        >
                                            <Download size={12} />
                                            {importandoLote ? 'Importando...' : `Importar Selecionados (${selPlaces.length})`}
                                        </button>
                                    )}
                                </div>
                            );
                        })()}

                        {buscando ? (
                            <div className="flex justify-center py-10"><LoadingSpinner label="Consultando Google Places..." size="sm" /></div>
                        ) : resultadosView.length === 0 && buscou ? (
                            <div className="text-center py-10 text-gray-400">
                                <Building2 size={40} className="mx-auto mb-3 opacity-30" />
                                <p className="text-sm">
                                    {kwhMin > 0 && resultados.length > 0
                                        ? `Nenhuma empresa com potencial ≥ ${kwhMin.toLocaleString('pt-BR')} kWh/mês. Reduza o filtro de kWh mín.`
                                        : 'Nenhuma empresa encontrada nessa região/categorias.'}
                                </p>
                            </div>
                        ) : (
                            <div className={`space-y-2 overflow-y-auto pr-1 ${mapaExpandido ? 'max-h-[220px]' : 'max-h-[480px]'}`}>
                                {resultadosView.slice(0, limiteLista).map(e => (
                                    <div key={e.place_id} onClick={() => focarNoMapa(e)} title="Clique para destacar no mapa"
                                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${destacado === e.place_id ? 'ring-2 ring-blue-400 border-blue-300' : ''} ${selPlaces.includes(e.place_id) ? 'bg-primary/5 border-primary/30' : 'bg-gray-50/60 dark:bg-gray-700/30 border-transparent hover:border-gray-200 dark:hover:border-gray-600'}`}>
                                        {e.status !== 'importado' && !e.in_crm && (
                                            <input
                                                type="checkbox"
                                                checked={selPlaces.includes(e.place_id)}
                                                onClick={ev => ev.stopPropagation()}
                                                onChange={() => setSelPlaces(prev => prev.includes(e.place_id) ? prev.filter(id => id !== e.place_id) : [...prev, e.place_id])}
                                                className="rounded text-primary focus:ring-primary shrink-0"
                                            />
                                        )}
                                        {/* Posição na rota calculada — é o que substitui a antiga
                                            linha "Sequência: 1. … → 2. …" do topo. */}
                                        {posicaoNaRota.get(e.place_id) != null && (
                                            <span className="w-6 h-6 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                                                {posicaoNaRota.get(e.place_id)}
                                            </span>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{e.nome}</p>
                                                {e.rating != null && (
                                                    <span className="flex items-center gap-0.5 text-[11px] font-semibold text-amber-500 shrink-0">
                                                        <Star size={11} fill="currentColor" /> {Number(e.rating).toFixed(1)}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{e.categoria}{e.endereco ? ` · ${e.endereco}` : ''}</p>
                                            {e.situacao && e.situacao !== 'ATIVA' && (
                                                <span className="inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                                    CNPJ {e.situacao}
                                                </span>
                                            )}
                                            {(() => {
                                                const c = cruzamento.get(e.place_id);
                                                if (!c) return null;
                                                const lf = c.listaFria;
                                                return (
                                                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                        <span
                                                            className="font-mono text-[11px] font-semibold text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-900/30 px-1.5 py-0.5 rounded"
                                                            title={`Mesma empresa na Lista Fria (${c.via === 'telefone' ? 'mesmo telefone' : 'nome parecido'}, a ${c.metros} m). O CNPJ vem de lá, sem busca paga.`}
                                                        >
                                                            Lista Fria · {maskCnpj(lf.documento)}
                                                        </span>
                                                        {lf.situacao && lf.situacao !== 'ATIVA' && (
                                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">CNPJ {lf.situacao}</span>
                                                        )}
                                                        {lf.status === 'importado' && (
                                                            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">já importada pela Lista Fria</span>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="flex items-center gap-1 text-[11px] font-bold text-blue-600 dark:text-blue-400">
                                                    <Zap size={11} /> {e.kwh_estimado ? `~${Math.round(e.kwh_estimado).toLocaleString('pt-BR')} kWh/mês (média do segmento)` : 'kWh n/d'}
                                                </span>
                                                {e.telefone && <span className="text-[11px] text-gray-400">{e.telefone}</span>}
                                            </div>
                                            {e.cnpj && (
                                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                    {e.enriquecimento_data?.cnpj_descoberta?.candidatos?.length ? (
                                                        <button
                                                            onClick={() => setModalCandidatos({ empresa: e, candidatos: e.enriquecimento_data!.cnpj_descoberta!.candidatos as CnpjCandidato[] })}
                                                            title="Conferir ou trocar o CNPJ vinculado"
                                                            className="font-mono text-[11px] font-semibold text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/30 px-1.5 py-0.5 rounded underline decoration-dotted underline-offset-2 hover:ring-1 hover:ring-violet-400"
                                                        >{maskCnpj(e.cnpj)}</button>
                                                    ) : (
                                                        <span className="font-mono text-[11px] font-semibold text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/30 px-1.5 py-0.5 rounded">{maskCnpj(e.cnpj)}</span>
                                                    )}
                                                    {e.enriquecimento_data?.cnpj_descoberta?.escolhido?.razao_social && (
                                                        <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[200px]">{e.enriquecimento_data.cnpj_descoberta.escolhido.razao_social}</span>
                                                    )}
                                                    {e.enriquecimento_status === 'completo' && (
                                                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">dono ok</span>
                                                    )}
                                                    {(e.enriquecimento_status === 'empresa' || e.enriquecimento_status === 'socios') && (
                                                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">enriquecido</span>
                                                    )}
                                                    {e.enriquecimento_status === 'falhou' && (
                                                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">enriq. falhou</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {e.status === 'importado' ? (
                                            <span className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 shrink-0">
                                                <Check size={12} /> Importado
                                            </span>
                                        ) : e.in_crm ? (
                                            <span className="px-3 py-1.5 text-[11px] font-bold rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                                                Já no CRM{e.dono_crm_id ? ` · ${nomeCurto(String(e.dono_crm_id))}` : ''}
                                            </span>
                                        ) : (
                                            <div className="flex flex-col gap-1.5 shrink-0" onClick={ev => ev.stopPropagation()}>
                                                {/* Só na base do Google: é ela que traz o lead sem
                                                    CNPJ. Na Lista Fria o CNPJ É a chave do cadastro,
                                                    então a busca reversa não tem o que descobrir. */}
                                                {/* Casou com a Lista Fria: CNPJ e dados já existem lá — nem
                                                    Descobrir (pago) nem Enriquecer fazem sentido */}
                                                {cruzamento.has(e.place_id) ? null : !e.cnpj && resultadosBase === 'google' ? (
                                                    <button
                                                        onClick={() => descobrir(e)}
                                                        disabled={descobrindo === e.place_id}
                                                        title={custoCnpj ? `Busca reversa na Casa dos Dados (~R$ ${custoCnpj.toFixed(2).replace('.', ',')})` : 'Descobrir CNPJ na Casa dos Dados'}
                                                        className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-full bg-violet-600 text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                                                    >
                                                        {descobrindo === e.place_id ? <Loader2 size={12} className="animate-spin" /> : <Fingerprint size={12} />}
                                                        {descobrindo === e.place_id ? 'Buscando...' : 'Descobrir CNPJ'}
                                                    </button>
                                                ) : e.enriquecimento_status !== 'completo' && (
                                                    <button
                                                        onClick={() => enriquecer(e)}
                                                        disabled={enriquecendo === e.place_id}
                                                        title="Enriquecer via Lemit (empresa → dono)"
                                                        className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-full bg-amber-500 text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                                                    >
                                                        {enriquecendo === e.place_id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                                        {enriquecendo === e.place_id ? 'Enriquecendo...' : 'Enriquecer'}
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => importar(e)}
                                                    disabled={importando === e.place_id || !firstStage}
                                                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-full bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                                                >
                                                    <Download size={12} /> {importando === e.place_id ? 'Importando...' : 'Importar'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {resultadosView.length > limiteLista && (
                                    <button
                                        onClick={() => setLimiteLista(n => n + 200)}
                                        className="w-full py-2 text-xs font-bold rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:border-primary/40 hover:text-primary"
                                    >
                                        Mostrar mais ({limiteLista} de {resultadosView.length} na lista — todas já estão no mapa)
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <UsageLogModal isOpen={showUsage} onClose={() => setShowUsage(false)} profiles={profiles} />

            <CnpjCandidatosModal
                isOpen={!!modalCandidatos}
                empresaNome={modalCandidatos?.empresa.nome}
                empresaEndereco={modalCandidatos?.empresa.endereco}
                empresaTelefone={modalCandidatos?.empresa.telefone}
                candidatos={modalCandidatos?.candidatos || []}
                cnpjAtual={modalCandidatos?.empresa.cnpj}
                escolhendo={escolhendoCnpj}
                onEscolher={escolherCandidato}
                onLimpar={modalCandidatos?.empresa.cnpj ? limparCnpj : undefined}
                onClose={() => setModalCandidatos(null)}
            />

            <RotaConfigModal
                isOpen={showRotaConfig}
                qtdLeads={loteSelecionado().length}
                kwhTotal={loteSelecionado().reduce((s, e) => s + Number(e.kwh_estimado || 0), 0)}
                destinosLabel={destNome}
                destinosCount={destinos().length}
                onClose={() => setShowRotaConfig(false)}
                onConfirmRota={agendarVisitas}
                onImportarSemRota={importarLote}
            />
        </div>
    );
};

export default ProspeccaoGeoView;
